import {
  AddAspects,
  AspectMap,
  MatchesRequiredAspects,
  NoPositiveAspects,
  PositiveAspects,
  SortByAspect,
  SubtractAspects,
  SubtractAspectsInplace,
} from "./aspects";
import { GetItemById } from "./items";
import { Principles } from "./principles";
import { Save } from "./save";
import { GetSkillById, Skill } from "./skills";
import {
  GetSlotablesFromSave,
  MatchesSlot,
  Slot,
  Slotable,
  Workstation,
} from "./workstation";

export interface Recipe {
  id: string;
  label: string;
  duration: number;

  skill: string;
  result: string;

  reqs: AspectMap;
}

// Literally only recipes with 2 outputs include "larva.hungry"!!
// Can just filter that one out and make this a SINGLE field.

export function GetRecipeById(id: string, recipes = Recipes): Recipe {
  const recipe = recipes.find((r) => r.id === id);
  if (!recipe) throw new Error(`Couldn't find recipe id: ${id}`);
  return recipe;
}

export function GetRecipesBySkill(skill: string, recipes = Recipes): Recipe[] {
  return recipes.filter((r) => r.skill === skill);
}

export function GetRecipesByResult(
  result: string,
  recipes = Recipes,
): Recipe[] {
  return recipes.filter((r) => r.result == result);
}

// OMG
export function FilterRecipesBySkills(skills: Skill[], recipes = Recipes) {
  return recipes.filter((recipe) =>
    skills.some((skill) => skill.id == recipe.skill),
  );
}

export function ToRecipeString(recipe: Recipe): string {
  return `${GetSkillById(recipe.skill).label}: ${Principles.filter(
    (p) => recipe.reqs[p],
  )
    .map((p) => `${p} ${recipe.reqs[p]}`)
    .join(", ")}`;
}

// This doesn't..
export function GetRequiredRecipeInputs(recipe: Recipe) {
  return Object.keys(recipe)
    .filter((key) => GetItemById(key))
    .noNulls();
}

interface RecipeCost extends AspectMap {
  duration: number;
}

const recipeCostMap = new Map<string, RecipeCost>();

// lol, 2 items are literally recursive!!
export function CalculateRecipeCost(recipe: Recipe): RecipeCost {
  const existingCost = recipeCostMap.get(recipe.id);
  if (existingCost) return { ...existingCost };

  // console.log("Checking cost for " + recipe.id, recipe.duration);
  let inputCost: RecipeCost = { ...recipe.reqs, duration: recipe.duration };
  const requiredInputs = GetRequiredRecipeInputs(recipe);
  if (!requiredInputs.length) return inputCost;

  // console.log(recipe.id + " requires", requiredInputs);

  for (const input of requiredInputs) {
    const inputRecipes = GetRecipesByResult(input);
    let minRecipeCost: RecipeCost = { duration: Number.MAX_SAFE_INTEGER };
    if (!inputRecipes.length) {
      console.log("Can't make this!", input);
      break;
    }

    for (const inputRecipe of inputRecipes) {
      const cost = CalculateRecipeCost(inputRecipe);
      if (cost.duration < minRecipeCost.duration) minRecipeCost = cost;
    }
    if (minRecipeCost.duration == Number.MAX_SAFE_INTEGER)
      console.log("Couldn't make this!", input);
    AddAspects(inputCost, minRecipeCost);
  }

  recipeCostMap.set(recipe.id, inputCost);
  return inputCost;
}

// idk this might not be the best way.
export function IsCraftable(id: string) {
  return GetRecipesByResult(id).some(
    (r) => CalculateRecipeCost(r).duration < Number.MAX_SAFE_INTEGER,
  );
}

export function Cost(id: string) {
  return Math.min(
    ...GetRecipesByResult(id).map((r) => CalculateRecipeCost(r).duration),
  );
}

type SlotMap = Map<Slot, Slotable>;
type WorkstationMap = Map<Workstation, SlotMap[]>;

// Get EVERY relevant combo that can make the recipe.
// Then sort them by cost and what you have available?
export function GetWaysToMakeRecipe(
  recipe: Recipe,
  save: Save,
): WorkstationMap {
  // DERP!
  const skill = save.skills.find((s) => s.id == recipe.skill);
  if (!skill) return new Map();
  // console.log("has skill!", skill);

  const workstationMap: WorkstationMap = new Map();
  for (const workstation of save.workstations) {
    if (workstationMap.size > 5) return workstationMap; // That's probably enough.

    // console.log("workstation", workstation);
    const remainingAspects = SubtractAspects(
      recipe.reqs,
      workstation.aspects || {},
    );
    const skillSlot = workstation.slots.find((slot) =>
      MatchesSlot(slot, skill),
    );
    if (!skillSlot) continue;
    // console.log("workstation has slot", skillSlot);
    SubtractAspectsInplace(remainingAspects, skill);
    const slotmaps = GetWaysToFillSlots(
      remainingAspects,
      workstation.slots.filter((s) => s !== skillSlot),
      GetSlotablesFromSave(save).filter((s) => s.id !== recipe.result),
    ).map((sub) => sub.set(skillSlot, skill));

    if (slotmaps.length) workstationMap.set(workstation, slotmaps);
  }

  return workstationMap;
}

function GetWaysToFillSlots(
  requiredAspects: AspectMap,
  slots: Slot[],
  slotables: Slotable[],
): SlotMap[] {
  if (NoPositiveAspects(requiredAspects)) {
    // console.log("Success!");
    return [new Map()];
  }
  if (!slots.length) return [];
  const matchingSlotables = slotables.filter(
    (slotable) =>
      MatchesRequiredAspects(requiredAspects, slotable) &&
      slots.some((slot) => MatchesSlot(slot, slotable)),
  );
  // console.log("matching", matchingSlotables.length);
  if (!matchingSlotables.length) return [];

  const results: SlotMap[] = [];
  // so for each slot, we could try the top N best items for each aspect?
  const aspects = PositiveAspects(requiredAspects);
  for (const { aspect } of aspects) {
    // take the top like.. 5 or so best items for the aspect
    const aspectSlotables = matchingSlotables
      .filter((slotable) => slotable[aspect])
      .sort(SortByAspect(aspect))
      .filter((_, i) => i < 10);

    for (const slotable of aspectSlotables) {
      for (const slot of slots) {
        // This is totally arbitrary, but seems to work.
        // lol unless it is impossible, in which case it will try EVERY combo!!
        // which is still STUPIDLY enormous.
        if (results.length > 10) return results;
        if (!MatchesSlot(slot, slotable)) continue;

        const subresults = GetWaysToFillSlots(
          SubtractAspects(requiredAspects, slotable),
          slots.filter((s) => s !== slot),
          slotables.filter((s) => s != slotable),
        );

        // Just merge them!
        for (const subresult of subresults) {
          subresult.set(slot, slotable);
          results.push(subresult);
        }

        // console.log("subresults", subresults.length, results.length);
      }
    }
  }

  return results;
}

export const Recipes: Recipe[] = [
  {
    id: "craft.keeper.sickle.eclipse_edge_perinculate_serpent-milk",
    reqs: {
      ability: 1,
      "s.sickle.eclipse": 1,
      perinculate: 1,
      edge: 15,
    },
    label: "Craft: Serpent-Milk",
    duration: 120,
    skill: "s.sickle.eclipse",
    result: "serpentmilk",
  },
  {
    id: "craft.keeper.edictsmartial_moon_confounding.parable_forbidden epic",
    reqs: {
      ability: 1,
      "s.edictsmartial": 1,
      "confounding.parable": 1,
      moon: 15,
    },
    label: "Craft: Forbidden Epic",
    duration: 120,
    skill: "s.edictsmartial",
    result: "forbidden.epic",
  },
  {
    id: "craft.keeper.sharps_edge_perinculate_serpent-milk",
    reqs: {
      ability: 1,
      "s.sharps": 1,
      perinculate: 1,
      edge: 15,
    },
    label: "Craft: Serpent-Milk",
    duration: 120,
    skill: "s.sharps",
    result: "serpentmilk",
  },
  {
    id: "craft.keeper.meniscatereflections_edge_perinculate_serpent-milk",
    reqs: {
      ability: 1,
      "s.meniscatereflections": 1,
      perinculate: 1,
      edge: 15,
    },
    label: "Craft: Serpent-Milk",
    duration: 120,
    skill: "s.meniscatereflections",
    result: "serpentmilk",
  },
  {
    id: "craft.keeper.disciplines.thescar_edge_perinculate_serpent-milk",
    reqs: {
      ability: 1,
      "s.disciplines.thescar": 1,
      perinculate: 1,
      edge: 15,
    },
    label: "Craft: Serpent-Milk",
    duration: 120,
    skill: "s.disciplines.thescar",
    result: "serpentmilk",
  },
  {
    id: "craft.keeper.serpents.venoms_moon_toxin.glassfinger_sacrament ascite",
    reqs: {
      ability: 1,
      "s.serpents.venoms": 1,
      "toxin.glassfinger": 1,
      moon: 15,
    },
    label: "Craft: Sacrament Ascite",
    duration: 120,
    skill: "s.serpents.venoms",
    result: "sacrament.ascite",
  },
  {
    id: "craft.keeper.disciplines.thehammer_edge_winning.move_invincible audacity",
    reqs: {
      ability: 1,
      "s.disciplines.thehammer": 1,
      "winning.move": 1,
      edge: 15,
    },
    label: "Craft: Invincible Audacity",
    duration: 120,
    skill: "s.disciplines.thehammer",
    result: "invincible.audacity",
  },
  {
    id: "craft.keeper.edictsmartial_edge_winning.move_invincible audacity",
    reqs: {
      ability: 1,
      "s.edictsmartial": 1,
      "winning.move": 1,
      edge: 15,
    },
    label: "Craft: Invincible Audacity",
    duration: 120,
    skill: "s.edictsmartial",
    result: "invincible.audacity",
  },
  {
    id: "craft.keeper.auroralcontemplations_edge_winning.move_invincible audacity",
    reqs: {
      ability: 1,
      "s.auroralcontemplations": 1,
      "winning.move": 1,
      edge: 15,
    },
    label: "Craft: Invincible Audacity",
    duration: 120,
    skill: "s.auroralcontemplations",
    result: "invincible.audacity",
  },
  {
    id: "craft.keeper.transformations.liberations_forge_essence.iotic_orpiment exultant",
    reqs: {
      ability: 1,
      "s.transformations.liberations": 1,
      "essence.iotic": 1,
      forge: 15,
    },
    label: "Craft: Orpiment Exultant",
    duration: 120,
    skill: "s.transformations.liberations",
    result: "orpiment.exultant",
  },
  {
    id: "craft.keeper.inks.power_forge_essence.iotic_orpiment exultant",
    reqs: {
      ability: 1,
      "s.inks.power": 1,
      "essence.iotic": 1,
      forge: 15,
    },
    label: "Craft: Orpiment Exultant",
    duration: 120,
    skill: "s.inks.power",
    result: "orpiment.exultant",
  },
  {
    id: "craft.keeper.anbary.lapidary_forge_essence.iotic_orpiment exultant",
    reqs: {
      ability: 1,
      "s.anbary.lapidary": 1,
      "essence.iotic": 1,
      forge: 15,
    },
    label: "Craft: Orpiment Exultant",
    duration: 120,
    skill: "s.anbary.lapidary",
    result: "orpiment.exultant",
  },
  {
    id: "craft.keeper.pyroglyphics_forge_wire.orichalcum_azoth",
    reqs: {
      ability: 1,
      "s.pyroglyphics": 1,
      "wire.orichalcum": 1,
      forge: 15,
    },
    label: "Craft: Azoth",
    duration: 120,
    skill: "s.pyroglyphics",
    result: "azoth",
  },
  {
    id: "craft.keeper.bells.brazieries_forge_wire.orichalcum_azoth",
    reqs: {
      ability: 1,
      "s.bells.brazieries": 1,
      "wire.orichalcum": 1,
      forge: 15,
    },
    label: "Craft: Azoth",
    duration: 120,
    skill: "s.bells.brazieries",
    result: "azoth",
  },
  {
    id: "craft.keeper.meniscatereflections_forge_wire.orichalcum_azoth",
    reqs: {
      ability: 1,
      "s.meniscatereflections": 1,
      "wire.orichalcum": 1,
      forge: 15,
    },
    label: "Craft: Azoth",
    duration: 120,
    skill: "s.meniscatereflections",
    result: "azoth",
  },
  {
    id: "craft.keeper.purifications.exaltations_forge_wire.orichalcum_azoth",
    reqs: {
      ability: 1,
      "s.purifications.exaltations": 1,
      "wire.orichalcum": 1,
      forge: 15,
    },
    label: "Craft: Azoth",
    duration: 120,
    skill: "s.purifications.exaltations",
    result: "azoth",
  },
  {
    id: "craft.keeper.disciplines.thehammer_forge_wire.orichalcum_azoth",
    reqs: {
      ability: 1,
      "s.disciplines.thehammer": 1,
      "wire.orichalcum": 1,
      forge: 15,
    },
    label: "Craft: Azoth",
    duration: 120,
    skill: "s.disciplines.thehammer",
    result: "azoth",
  },
  {
    id: "craft.keeper.door.wall_forge_wire.orichalcum_azoth",
    reqs: {
      ability: 1,
      "s.door.wall": 1,
      "wire.orichalcum": 1,
      forge: 15,
    },
    label: "Craft: Azoth",
    duration: 120,
    skill: "s.door.wall",
    result: "azoth",
  },
  {
    id: "craft.keeper.orchids.narcotics_grail_rubywise.ruin_sacrament calicite",
    reqs: {
      ability: 1,
      "s.orchids.narcotics": 1,
      "rubywise.ruin": 1,
      grail: 15,
    },
    label: "Craft: Sacrament Calicite",
    duration: 120,
    skill: "s.orchids.narcotics",
    result: "sacrament.calicite",
  },
  {
    id: "craft.keeper.applebrighteuphonies_grail_rubywise.ruin_sacrament calicite",
    reqs: {
      ability: 1,
      "s.applebrighteuphonies": 1,
      "rubywise.ruin": 1,
      grail: 15,
    },
    label: "Craft: Sacrament Calicite",
    duration: 120,
    skill: "s.applebrighteuphonies",
    result: "sacrament.calicite",
  },
  {
    id: "craft.keeper.leaves.thorns_grail_rubywise.ruin_sacrament calicite",
    reqs: {
      ability: 1,
      "s.leaves.thorns": 1,
      "rubywise.ruin": 1,
      grail: 15,
    },
    label: "Craft: Sacrament Calicite",
    duration: 120,
    skill: "s.leaves.thorns",
    result: "sacrament.calicite",
  },
  {
    id: "craft.keeper.seastories_grail_rubywise.ruin_sacrament calicite",
    reqs: {
      ability: 1,
      "s.seastories": 1,
      "rubywise.ruin": 1,
      grail: 15,
    },
    label: "Craft: Sacrament Calicite",
    duration: 120,
    skill: "s.seastories",
    result: "sacrament.calicite",
  },
  {
    id: "craft.keeper.tridesmahiera_grail_rubywise.ruin_sacrament calicite",
    reqs: {
      ability: 1,
      "s.tridesmahiera": 1,
      "rubywise.ruin": 1,
      grail: 15,
    },
    label: "Craft: Sacrament Calicite",
    duration: 120,
    skill: "s.tridesmahiera",
    result: "sacrament.calicite",
  },
  {
    id: "craft.keeper.resurgences.emergences_grail_pale.mommet_flushed mommet",
    reqs: {
      ability: 1,
      "s.resurgences.emergences": 1,
      "pale.mommet": 1,
      grail: 15,
    },
    label: "Craft: Flushed Mommet",
    duration: 120,
    skill: "s.resurgences.emergences",
    result: "flushed.mommet",
  },
  {
    id: "craft.keeper.furs.feathers_grail_pale.mommet_flushed mommet",
    reqs: {
      ability: 1,
      "s.furs.feathers": 1,
      "pale.mommet": 1,
      grail: 15,
    },
    label: "Craft: Flushed Mommet",
    duration: 120,
    skill: "s.furs.feathers",
    result: "flushed.mommet",
  },
  {
    id: "craft.keeper.thegreatsignsandthegreatscars_grail_pale.mommet_flushed mommet",
    reqs: {
      ability: 1,
      "s.thegreatsignsandthegreatscars": 1,
      "pale.mommet": 1,
      grail: 15,
    },
    label: "Craft: Flushed Mommet",
    duration: 120,
    skill: "s.thegreatsignsandthegreatscars",
    result: "flushed.mommet",
  },
  {
    id: "craft.keeper.pentiments.precursors_grail_pale.mommet_flushed mommet",
    reqs: {
      ability: 1,
      "s.pentiments.precursors": 1,
      "pale.mommet": 1,
      grail: 15,
    },
    label: "Craft: Flushed Mommet",
    duration: 120,
    skill: "s.pentiments.precursors",
    result: "flushed.mommet",
  },
  {
    id: "craft.keeper.desires.dissolutions_grail_pale.mommet_flushed mommet",
    reqs: {
      ability: 1,
      "s.desires.dissolutions": 1,
      "pale.mommet": 1,
      grail: 15,
    },
    label: "Craft: Flushed Mommet",
    duration: 120,
    skill: "s.desires.dissolutions",
    result: "flushed.mommet",
  },
  {
    id: "craft.keeper.weaving.knotworking_heart_frithweft_swaddled thunder",
    reqs: {
      ability: 1,
      "s.weaving.knotworking": 1,
      frithweft: 1,
      heart: 15,
    },
    label: "Craft: Swaddled Thunder",
    duration: 120,
    skill: "s.weaving.knotworking",
    result: "swaddled.thunder",
  },
  {
    id: "craft.keeper.stitching.binding_heart_frithweft_swaddled thunder",
    reqs: {
      ability: 1,
      "s.stitching.binding": 1,
      frithweft: 1,
      heart: 15,
    },
    label: "Craft: Swaddled Thunder",
    duration: 120,
    skill: "s.stitching.binding",
    result: "swaddled.thunder",
  },
  {
    id: "craft.keeper.quenchings.quellings_heart_frithweft_swaddled thunder",
    reqs: {
      ability: 1,
      "s.quenchings.quellings": 1,
      frithweft: 1,
      heart: 15,
    },
    label: "Craft: Swaddled Thunder",
    duration: 120,
    skill: "s.quenchings.quellings",
    result: "swaddled.thunder",
  },
  {
    id: "craft.keeper.drums.dances_heart_music.thunderskin_swaddled thunder",
    reqs: {
      ability: 1,
      "s.drums.dances": 1,
      "music.thunderskin": 1,
      heart: 15,
    },
    label: "Craft: Swaddled Thunder",
    duration: 120,
    skill: "s.drums.dances",
    result: "swaddled.thunder",
  },
  {
    id: "craft.keeper.strings.songs_heart_music.thunderskin_swaddled thunder",
    reqs: {
      ability: 1,
      "s.strings.songs": 1,
      "music.thunderskin": 1,
      heart: 15,
    },
    label: "Craft: Swaddled Thunder",
    duration: 120,
    skill: "s.strings.songs",
    result: "swaddled.thunder",
  },
  {
    id: "craft.keeper.maggephenemysteries_heart_liquid.gideon_january sanguinary",
    reqs: {
      ability: 1,
      "s.maggephenemysteries": 1,
      "liquid.gideon": 1,
      heart: 15,
    },
    label: "Craft: January Sanguinary",
    duration: 120,
    skill: "s.maggephenemysteries",
    result: "january.sanguinary",
  },
  {
    id: "craft.keeper.surgeries.exsanguinations_heart_liquid.gideon_january sanguinary",
    reqs: {
      ability: 1,
      "s.surgeries.exsanguinations": 1,
      "liquid.gideon": 1,
      heart: 15,
    },
    label: "Craft: January Sanguinary",
    duration: 120,
    skill: "s.surgeries.exsanguinations",
    result: "january.sanguinary",
  },
  {
    id: "craft.keeper.herbs.infusions_heart_liquid.gideon_january sanguinary",
    reqs: {
      ability: 1,
      "s.herbs.infusions": 1,
      "liquid.gideon": 1,
      heart: 15,
    },
    label: "Craft: January Sanguinary",
    duration: 120,
    skill: "s.herbs.infusions",
    result: "january.sanguinary",
  },
  {
    id: "craft.keeper.surgeries.exsanguinations_knock_toxin.glassfinger_january sanguinary",
    reqs: {
      ability: 1,
      "s.surgeries.exsanguinations": 1,
      "toxin.glassfinger": 1,
      knock: 15,
    },
    label: "Craft: January Sanguinary",
    duration: 120,
    skill: "s.surgeries.exsanguinations",
    result: "january.sanguinary",
  },
  {
    id: "craft.keeper.edictsinviolable_heart_liquid.gideon_january sanguinary",
    reqs: {
      ability: 1,
      "s.edictsinviolable": 1,
      "liquid.gideon": 1,
      heart: 15,
    },
    label: "Craft: January Sanguinary",
    duration: 120,
    skill: "s.edictsinviolable",
    result: "january.sanguinary",
  },
  {
    id: "craft.keeper.glassblowing.vesselcrafting_knock_gervinite_chronsichord",
    reqs: {
      ability: 1,
      "s.glassblowing.vesselcrafting": 1,
      gervinite: 1,
      knock: 15,
    },
    label: "Craft: Chronsichord",
    duration: 120,
    skill: "s.glassblowing.vesselcrafting",
    result: "chronsicord",
  },
  {
    id: "craft.keeper.lockworks.clockworks_knock_gervinite_chronsichord",
    reqs: {
      ability: 1,
      "s.lockworks.clockworks": 1,
      gervinite: 1,
      knock: 15,
    },
    label: "Craft: Chronsichord",
    duration: 120,
    skill: "s.lockworks.clockworks",
    result: "chronsicord",
  },
  {
    id: "craft.keeper.preliminalmeter_knock_gervinite_chronsichord",
    reqs: {
      ability: 1,
      "s.preliminalmeter": 1,
      gervinite: 1,
      knock: 15,
    },
    label: "Craft: Chronsichord",
    duration: 120,
    skill: "s.preliminalmeter",
    result: "chronsicord",
  },
  {
    id: "craft.keeper.thegreatsignsandthegreatscars_knock_gervinite_chronsichord",
    reqs: {
      ability: 1,
      "s.thegreatsignsandthegreatscars": 1,
      gervinite: 1,
      knock: 15,
    },
    label: "Craft: Chronsichord",
    duration: 120,
    skill: "s.thegreatsignsandthegreatscars",
    result: "chronsicord",
  },
  {
    id: "craft.keeper.spices.savours_forge_honeyscar.jasmine_azoth",
    reqs: {
      ability: 1,
      "s.spices.savours": 1,
      "honeyscar.jasmine": 1,
      forge: 15,
    },
    label: "Craft: Azoth",
    duration: 120,
    skill: "s.spices.savours",
    result: "azoth",
  },
  {
    id: "craft.keeper.glassblowing.vesselcrafting_knock_toxin.glassfinger_sacrament ascite",
    reqs: {
      ability: 1,
      "s.glassblowing.vesselcrafting": 1,
      "toxin.glassfinger": 1,
      knock: 15,
    },
    label: "Craft: Sacrament Ascite",
    duration: 120,
    skill: "s.glassblowing.vesselcrafting",
    result: "sacrament.ascite",
  },
  {
    id: "craft.keeper.door.wall_knock_toxin.glassfinger_sacrament ascite",
    reqs: {
      ability: 1,
      "s.door.wall": 1,
      "toxin.glassfinger": 1,
      knock: 15,
    },
    label: "Craft: Sacrament Ascite",
    duration: 120,
    skill: "s.door.wall",
    result: "sacrament.ascite",
  },
  {
    id: "craft.keeper.solutions.separations_knock_toxin.glassfinger_sacrament ascite",
    reqs: {
      ability: 1,
      "s.solutions.separations": 1,
      "toxin.glassfinger": 1,
      knock: 15,
    },
    label: "Craft: Sacrament Ascite",
    duration: 120,
    skill: "s.solutions.separations",
    result: "sacrament.ascite",
  },
  {
    id: "craft.keeper.horns.ivories_knock_toxin.glassfinger_sacrament ascite",
    reqs: {
      ability: 1,
      "s.horns.ivories": 1,
      "toxin.glassfinger": 1,
      knock: 15,
    },
    label: "Craft: Sacrament Ascite",
    duration: 120,
    skill: "s.horns.ivories",
    result: "sacrament.ascite",
  },
  {
    id: "craft.keeper.edictsliminal_knock_toxin.glassfinger_sacrament ascite",
    reqs: {
      ability: 1,
      "s.edictsliminal": 1,
      "toxin.glassfinger": 1,
      knock: 15,
    },
    label: "Craft: Sacrament Ascite",
    duration: 120,
    skill: "s.edictsliminal",
    result: "sacrament.ascite",
  },
  {
    id: "craft.keeper.inks.revelation_lantern_essence.xanthotic_uzult",
    reqs: {
      ability: 1,
      "s.inks.revelation": 1,
      "essence.xanthotic": 1,
      lantern: 15,
    },
    label: "Craft: Uzult",
    duration: 120,
    skill: "s.inks.revelation",
    result: "uzult",
  },
  {
    id: "craft.keeper.sacrasolisinvicti_lantern_essence.xanthotic_uzult",
    reqs: {
      ability: 1,
      "s.sacrasolisinvicti": 1,
      "essence.xanthotic": 1,
      lantern: 15,
    },
    label: "Craft: Uzult",
    duration: 120,
    skill: "s.sacrasolisinvicti",
    result: "uzult",
  },
  {
    id: "craft.keeper.pyroglyphics_lantern_ichor.vitreous_ashartine",
    reqs: {
      ability: 1,
      "s.pyroglyphics": 1,
      "ichor.vitreous": 1,
      lantern: 15,
    },
    label: "Craft: Ashartine",
    duration: 120,
    skill: "s.pyroglyphics",
    result: "ashartine",
  },
  {
    id: "craft.keeper.watchmansparadoxes_lantern_ichor.vitreous_ashartine",
    reqs: {
      ability: 1,
      "s.watchmansparadoxes": 1,
      "ichor.vitreous": 1,
      lantern: 15,
    },
    label: "Craft: Ashartine",
    duration: 120,
    skill: "s.watchmansparadoxes",
    result: "ashartine",
  },
  {
    id: "craft.keeper.glaziery.lightsmithing_lantern_ichor.vitreous_ashartine",
    reqs: {
      ability: 1,
      "s.glaziery.lightsmithing": 1,
      "ichor.vitreous": 1,
      lantern: 15,
    },
    label: "Craft: Ashartine",
    duration: 120,
    skill: "s.glaziery.lightsmithing",
    result: "ashartine",
  },
  {
    id: "craft.keeper.watchmansparadoxes_lantern_essence.xanthotic_uzult",
    reqs: {
      ability: 1,
      "s.watchmansparadoxes": 1,
      "essence.xanthotic": 1,
      lantern: 15,
    },
    label: "Craft: Uzult",
    duration: 120,
    skill: "s.watchmansparadoxes",
    result: "uzult",
  },
  {
    id: "craft.keeper.anbary.lapidary_sky_ichor.vitreous_ashartine",
    reqs: {
      ability: 1,
      "s.anbary.lapidary": 1,
      "ichor.vitreous": 1,
      sky: 15,
    },
    label: "Craft: Ashartine",
    duration: 120,
    skill: "s.anbary.lapidary",
    result: "ashartine",
  },
  {
    id: "craft.keeper.desires.dissolutions_moon_asimel_black sapphire wash",
    reqs: {
      ability: 1,
      "s.desires.dissolutions": 1,
      asimel: 1,
      moon: 15,
    },
    label: "Craft: Black Sapphire Wash",
    duration: 120,
    skill: "s.desires.dissolutions",
    result: "black.sapphire.wash",
  },
  {
    id: "craft.keeper.putrefactions.calcinations_moon_asimel_black sapphire wash",
    reqs: {
      ability: 1,
      "s.putrefactions.calcinations": 1,
      asimel: 1,
      moon: 15,
    },
    label: "Craft: Black Sapphire Wash",
    duration: 120,
    skill: "s.putrefactions.calcinations",
    result: "black.sapphire.wash",
  },
  {
    id: "craft.keeper.seastories_moon_asimel_black sapphire wash",
    reqs: {
      ability: 1,
      "s.seastories": 1,
      asimel: 1,
      moon: 15,
    },
    label: "Craft: Black Sapphire Wash",
    duration: 120,
    skill: "s.seastories",
    result: "black.sapphire.wash",
  },
  {
    id: "craft.keeper.pearl.tide_moon_asimel_black sapphire wash",
    reqs: {
      ability: 1,
      "s.pearl.tide": 1,
      asimel: 1,
      moon: 15,
    },
    label: "Craft: Black Sapphire Wash",
    duration: 120,
    skill: "s.pearl.tide",
    result: "black.sapphire.wash",
  },
  {
    id: "craft.keeper.tridesmahiera_moon_asimel_black sapphire wash",
    reqs: {
      ability: 1,
      "s.tridesmahiera": 1,
      asimel: 1,
      moon: 15,
    },
    label: "Craft: Black Sapphire Wash",
    duration: 120,
    skill: "s.tridesmahiera",
    result: "black.sapphire.wash",
  },
  {
    id: "craft.keeper.wolfstories_moon_stymphling_wormwood dream",
    reqs: {
      ability: 1,
      "s.wolfstories": 1,
      stymphling: 1,
      moon: 15,
    },
    label: "Craft: Wormwood Dream",
    duration: 120,
    skill: "s.wolfstories",
    result: "wormwood.dream",
  },
  {
    id: "craft.keeper.edictsinviolable_moon_stymphling_wormwood dream",
    reqs: {
      ability: 1,
      "s.edictsinviolable": 1,
      stymphling: 1,
      moon: 15,
    },
    label: "Craft: Wormwood Dream",
    duration: 120,
    skill: "s.edictsinviolable",
    result: "wormwood.dream",
  },
  {
    id: "craft.keeper.rhyme.remembrance_moon_stymphling_wormwood dream",
    reqs: {
      ability: 1,
      "s.rhyme.remembrance": 1,
      stymphling: 1,
      moon: 15,
    },
    label: "Craft: Wormwood Dream",
    duration: 120,
    skill: "s.rhyme.remembrance",
    result: "wormwood.dream",
  },
  {
    id: "craft.keeper.sickle.eclipse_moon_stymphling_wormwood dream",
    reqs: {
      ability: 1,
      "s.sickle.eclipse": 1,
      stymphling: 1,
      moon: 15,
    },
    label: "Craft: Wormwood Dream",
    duration: 120,
    skill: "s.sickle.eclipse",
    result: "wormwood.dream",
  },
  {
    id: "craft.keeper.snowstories_moon_stymphling_wormwood dream",
    reqs: {
      ability: 1,
      "s.snowstories": 1,
      stymphling: 1,
      moon: 15,
    },
    label: "Craft: Wormwood Dream",
    duration: 120,
    skill: "s.snowstories",
    result: "wormwood.dream",
  },
  {
    id: "craft.keeper.solutions.separations_moth_honey_skinshuck mead",
    reqs: {
      ability: 1,
      "s.solutions.separations": 1,
      honey: 1,
      moth: 15,
    },
    label: "Craft: Skinshuck Mead",
    duration: 120,
    skill: "s.solutions.separations",
    result: "skinshuck.mead",
  },
  {
    id: "craft.keeper.edictsliminal_moth_honey_skinshuck mead",
    reqs: {
      ability: 1,
      "s.edictsliminal": 1,
      honey: 1,
      moth: 15,
    },
    label: "Craft: Skinshuck Mead",
    duration: 120,
    skill: "s.edictsliminal",
    result: "skinshuck.mead",
  },
  {
    id: "craft.keeper.sacralimiae_moth_honey_skinshuck mead",
    reqs: {
      ability: 1,
      "s.sacralimiae": 1,
      honey: 1,
      moth: 15,
    },
    label: "Craft: Skinshuck Mead",
    duration: 120,
    skill: "s.sacralimiae",
    result: "skinshuck.mead",
  },
  {
    id: "craft.keeper.rites.theroots_moth_honey_skinshuck mead",
    reqs: {
      ability: 1,
      "s.rites.theroots": 1,
      honey: 1,
      moth: 15,
    },
    label: "Craft: Skinshuck Mead",
    duration: 120,
    skill: "s.rites.theroots",
    result: "skinshuck.mead",
  },
  {
    id: "craft.keeper.pearl.tide_rose_lenten.rose_porphyrine",
    reqs: {
      ability: 1,
      "s.pearl.tide": 1,
      "lenten.rose": 1,
      rose: 15,
    },
    label: "Craft: Porphyrine",
    duration: 120,
    skill: "s.pearl.tide",
    result: "porphyrine",
  },
  {
    id: "craft.keeper.insects.nectars_moon_larva.chimeric_perilous imago",
    reqs: {
      ability: 1,
      "s.insects.nectars": 1,
      "larva.chimeric": 1,
      moon: 15,
    },
    label: "Craft: Perilous Imago",
    duration: 120,
    skill: "s.insects.nectars",
    result: "imago.perilous",
  },
  {
    id: "craft.keeper.resurgences.emergences_moth_larva.chimeric_perilous imago",
    reqs: {
      ability: 1,
      "s.resurgences.emergences": 1,
      "larva.chimeric": 1,
      moth: 15,
    },
    label: "Craft: Perilous Imago",
    duration: 120,
    skill: "s.resurgences.emergences",
    result: "imago.perilous",
  },
  {
    id: "craft.keeper.transformations.liberations_moth_larva.chimeric_perilous imago",
    reqs: {
      ability: 1,
      "s.transformations.liberations": 1,
      "larva.chimeric": 1,
      moth: 15,
    },
    label: "Craft: Perilous Imago",
    duration: 120,
    skill: "s.transformations.liberations",
    result: "imago.perilous",
  },
  {
    id: "craft.keeper.drums.dances_nectar_pyrus.auricalcinus_sacrament malachite",
    reqs: {
      ability: 1,
      "s.drums.dances": 1,
      "pyrus.auricalcinus": 1,
      nectar: 15,
    },
    label: "Craft: Sacrament Malachite",
    duration: 120,
    skill: "s.drums.dances",
    result: "sacrament.malachite",
  },
  {
    id: "craft.keeper.insects.nectars_nectar_leathy_marakat",
    reqs: {
      ability: 1,
      "s.insects.nectars": 1,
      leathy: 1,
      nectar: 15,
    },
    label: "Craft: Marakat",
    duration: 120,
    skill: "s.insects.nectars",
    result: "marakat",
  },
  {
    id: "craft.keeper.inks.power_nectar_leathy_marakat",
    reqs: {
      ability: 1,
      "s.inks.power": 1,
      leathy: 1,
      nectar: 15,
    },
    label: "Craft: Marakat",
    duration: 120,
    skill: "s.inks.power",
    result: "marakat",
  },
  {
    id: "craft.keeper.tridesmahiera_nectar_leathy_marakat",
    reqs: {
      ability: 1,
      "s.tridesmahiera": 1,
      leathy: 1,
      nectar: 15,
    },
    label: "Craft: Marakat",
    duration: 120,
    skill: "s.tridesmahiera",
    result: "marakat",
  },
  {
    id: "craft.keeper.spices.savours_nectar_pyrus.auricalcinus_sacrament malachite",
    reqs: {
      ability: 1,
      "s.spices.savours": 1,
      "pyrus.auricalcinus": 1,
      nectar: 15,
    },
    label: "Craft: Sacrament Malachite",
    duration: 120,
    skill: "s.spices.savours",
    result: "sacrament.malachite",
  },
  {
    id: "craft.keeper.leaves.thorns_nectar_pyrus.auricalcinus_sacrament malachite",
    reqs: {
      ability: 1,
      "s.leaves.thorns": 1,
      "pyrus.auricalcinus": 1,
      nectar: 15,
    },
    label: "Craft: Sacrament Malachite",
    duration: 120,
    skill: "s.leaves.thorns",
    result: "sacrament.malachite",
  },
  {
    id: "craft.keeper.rites.theroots_nectar_pyrus.auricalcinus_sacrament malachite",
    reqs: {
      ability: 1,
      "s.rites.theroots": 1,
      "pyrus.auricalcinus": 1,
      nectar: 15,
    },
    label: "Craft: Sacrament Malachite",
    duration: 120,
    skill: "s.rites.theroots",
    result: "sacrament.malachite",
  },
  {
    id: "craft.keeper.herbs.infusions_nectar_pyrus.auricalcinus_sacrament malachite",
    reqs: {
      ability: 1,
      "s.herbs.infusions": 1,
      "pyrus.auricalcinus": 1,
      nectar: 15,
    },
    label: "Craft: Sacrament Malachite",
    duration: 120,
    skill: "s.herbs.infusions",
    result: "sacrament.malachite",
  },
  {
    id: "craft.keeper.coil.chasm_nectar_pyrus.auricalcinus_sacrament malachite",
    reqs: {
      ability: 1,
      "s.coil.chasm": 1,
      "pyrus.auricalcinus": 1,
      nectar: 15,
    },
    label: "Craft: Sacrament Malachite",
    duration: 120,
    skill: "s.coil.chasm",
    result: "sacrament.malachite",
  },
  {
    id: "craft.keeper.inks.revelation_rose_ichor.auroral_porphyrine",
    reqs: {
      ability: 1,
      "s.inks.revelation": 1,
      "ichor.auroral": 1,
      rose: 15,
    },
    label: "Craft: Porphyrine",
    duration: 120,
    skill: "s.inks.revelation",
    result: "porphyrine",
  },
  {
    id: "craft.keeper.auroralcontemplations_lantern_ichor.auroral_porphyrine",
    reqs: {
      ability: 1,
      "s.auroralcontemplations": 1,
      "ichor.auroral": 1,
      lantern: 15,
    },
    label: "Craft: Porphyrine",
    duration: 120,
    skill: "s.auroralcontemplations",
    result: "porphyrine",
  },
  {
    id: "craft.keeper.preliminalmeter_rose_enduring.reflection_horizon-sight",
    reqs: {
      ability: 1,
      "s.preliminalmeter": 1,
      "enduring.reflection": 1,
      rose: 15,
    },
    label: "Craft: Horizon-Sight",
    duration: 120,
    skill: "s.preliminalmeter",
    result: "horizonsight",
  },
  {
    id: "craft.keeper.sandstories_rose_enduring.reflection_horizon-sight",
    reqs: {
      ability: 1,
      "s.sandstories": 1,
      "enduring.reflection": 1,
      rose: 15,
    },
    label: "Craft: Horizon-Sight",
    duration: 120,
    skill: "s.sandstories",
    result: "horizonsight",
  },
  {
    id: "craft.keeper.skystories_rose_enduring.reflection_horizon-sight",
    reqs: {
      ability: 1,
      "s.skystories": 1,
      "enduring.reflection": 1,
      rose: 15,
    },
    label: "Craft: Horizon-Sight",
    duration: 120,
    skill: "s.skystories",
    result: "horizonsight",
  },
  {
    id: "craft.keeper.path.pilgrim_rose_enduring.reflection_horizon-sight",
    reqs: {
      ability: 1,
      "s.path.pilgrim": 1,
      "enduring.reflection": 1,
      rose: 15,
    },
    label: "Craft: Horizon-Sight",
    duration: 120,
    skill: "s.path.pilgrim",
    result: "horizonsight",
  },
  {
    id: "craft.keeper.sandstories_rose_ichor.auroral_porphyrine",
    reqs: {
      ability: 1,
      "s.sandstories": 1,
      "ichor.auroral": 1,
      rose: 15,
    },
    label: "Craft: Porphyrine",
    duration: 120,
    skill: "s.sandstories",
    result: "porphyrine",
  },
  {
    id: "craft.keeper.stonestories_scale_old.moment_earthquake name",
    reqs: {
      ability: 1,
      "s.stonestories": 1,
      "old.moment": 1,
      scale: 15,
    },
    label: "Craft: Earthquake Name",
    duration: 120,
    skill: "s.stonestories",
    result: "name.earthquake",
  },
  {
    id: "craft.keeper.coil.chasm_scale_old.moment_earthquake name",
    reqs: {
      ability: 1,
      "s.coil.chasm": 1,
      "old.moment": 1,
      scale: 15,
    },
    label: "Craft: Earthquake Name",
    duration: 120,
    skill: "s.coil.chasm",
    result: "name.earthquake",
  },
  {
    id: "craft.keeper.hill.hollow_scale_old.moment_earthquake name",
    reqs: {
      ability: 1,
      "s.hill.hollow": 1,
      "old.moment": 1,
      scale: 15,
    },
    label: "Craft: Earthquake Name",
    duration: 120,
    skill: "s.hill.hollow",
    result: "name.earthquake",
  },
  {
    id: "craft.keeper.disciplines.thescar_lantern_old.moment_earthquake name",
    reqs: {
      ability: 1,
      "s.disciplines.thescar": 1,
      "old.moment": 1,
      lantern: 15,
    },
    label: "Craft: Earthquake Name",
    duration: 120,
    skill: "s.disciplines.thescar",
    result: "name.earthquake",
  },
  {
    id: "craft.keeper.wolfstories_scale_old.moment_earthquake name",
    reqs: {
      ability: 1,
      "s.wolfstories": 1,
      "old.moment": 1,
      scale: 15,
    },
    label: "Craft: Earthquake Name",
    duration: 120,
    skill: "s.wolfstories",
    result: "name.earthquake",
  },
  {
    id: "craft.keeper.inks.containment_scale_relic.living_houndsgall",
    reqs: {
      ability: 1,
      "s.inks.containment": 1,
      "relic.living": 1,
      scale: 15,
    },
    label: "Craft: Houndsgall",
    duration: 120,
    skill: "s.inks.containment",
    result: "houndsgall",
  },
  {
    id: "craft.keeper.horns.ivories_scale_relic.living_houndsgall",
    reqs: {
      ability: 1,
      "s.horns.ivories": 1,
      "relic.living": 1,
      scale: 15,
    },
    label: "Craft: Houndsgall",
    duration: 120,
    skill: "s.horns.ivories",
    result: "houndsgall",
  },
  {
    id: "craft.keeper.path.pilgrim_scale_relic.living_houndsgall",
    reqs: {
      ability: 1,
      "s.path.pilgrim": 1,
      "relic.living": 1,
      scale: 15,
    },
    label: "Craft: Houndsgall",
    duration: 120,
    skill: "s.path.pilgrim",
    result: "houndsgall",
  },
  {
    id: "craft.keeper.pentiments.precursors_scale_relic.living_houndsgall",
    reqs: {
      ability: 1,
      "s.pentiments.precursors": 1,
      "relic.living": 1,
      scale: 15,
    },
    label: "Craft: Houndsgall",
    duration: 120,
    skill: "s.pentiments.precursors",
    result: "houndsgall",
  },
  {
    id: "craft.keeper.serpents.venoms_scale_perinculate_serpent-milk",
    reqs: {
      ability: 1,
      "s.serpents.venoms": 1,
      perinculate: 1,
      scale: 15,
    },
    label: "Craft: Serpent-Milk",
    duration: 120,
    skill: "s.serpents.venoms",
    result: "serpentmilk",
  },
  {
    id: "craft.keeper.strings.songs_sky_mazarine.fife_wind-in-waiting",
    reqs: {
      ability: 1,
      "s.strings.songs": 1,
      "mazarine.fife": 1,
      sky: 15,
    },
    label: "Craft: Wind-in-Waiting",
    duration: 120,
    skill: "s.strings.songs",
    result: "windinwaiting",
  },
  {
    id: "craft.keeper.skystories_sky_mazarine.fife_wind-in-waiting",
    reqs: {
      ability: 1,
      "s.skystories": 1,
      "mazarine.fife": 1,
      sky: 15,
    },
    label: "Craft: Wind-in-Waiting",
    duration: 120,
    skill: "s.skystories",
    result: "windinwaiting",
  },
  {
    id: "craft.keeper.sacrasolisinvicti_sky_mazarine.fife_wind-in-waiting",
    reqs: {
      ability: 1,
      "s.sacrasolisinvicti": 1,
      "mazarine.fife": 1,
      sky: 15,
    },
    label: "Craft: Wind-in-Waiting",
    duration: 120,
    skill: "s.sacrasolisinvicti",
    result: "windinwaiting",
  },
  {
    id: "craft.keeper.furs.feathers_sky_mazarine.fife_wind-in-waiting",
    reqs: {
      ability: 1,
      "s.furs.feathers": 1,
      "mazarine.fife": 1,
      sky: 15,
    },
    label: "Craft: Wind-in-Waiting",
    duration: 120,
    skill: "s.furs.feathers",
    result: "windinwaiting",
  },
  {
    id: "craft.keeper.lockworks.clockworks_sky_wire.silver_chronsichord",
    reqs: {
      ability: 1,
      "s.lockworks.clockworks": 1,
      "wire.silver": 1,
      sky: 15,
    },
    label: "Craft: Chronsichord",
    duration: 120,
    skill: "s.lockworks.clockworks",
    result: "chronsicord",
  },
  {
    id: "craft.keeper.ouranoscopy_sky_ascendant.harmony_didumos",
    reqs: {
      ability: 1,
      "s.ouranoscopy": 1,
      "ascendant.harmony": 1,
      sky: 15,
    },
    label: "Craft: Didumos",
    duration: 120,
    skill: "s.ouranoscopy",
    result: "didumos",
  },
  {
    id: "craft.keeper.sights.sensations_sky_ascendant.harmony_didumos",
    reqs: {
      ability: 1,
      "s.sights.sensations": 1,
      "ascendant.harmony": 1,
      sky: 15,
    },
    label: "Craft: Didumos",
    duration: 120,
    skill: "s.sights.sensations",
    result: "didumos",
  },
  {
    id: "craft.keeper.bells.brazieries_sky_ascendant.harmony_didumos",
    reqs: {
      ability: 1,
      "s.bells.brazieries": 1,
      "ascendant.harmony": 1,
      sky: 15,
    },
    label: "Craft: Didumos",
    duration: 120,
    skill: "s.bells.brazieries",
    result: "didumos",
  },
  {
    id: "craft.keeper.applebrighteuphonies_sky_ascendant.harmony_didumos",
    reqs: {
      ability: 1,
      "s.applebrighteuphonies": 1,
      "ascendant.harmony": 1,
      sky: 15,
    },
    label: "Craft: Didumos",
    duration: 120,
    skill: "s.applebrighteuphonies",
    result: "didumos",
  },
  {
    id: "craft.keeper.sacralimiae_sky_ascendant.harmony_didumos",
    reqs: {
      ability: 1,
      "s.sacralimiae": 1,
      "ascendant.harmony": 1,
      sky: 15,
    },
    label: "Craft: Didumos",
    duration: 120,
    skill: "s.sacralimiae",
    result: "didumos",
  },
  {
    id: "craft.keeper.inks.containment_winter_liquid.solomon_nillycant",
    reqs: {
      ability: 1,
      "s.inks.containment": 1,
      "liquid.solomon": 1,
      winter: 15,
    },
    label: "Craft: Nillycant",
    duration: 120,
    skill: "s.inks.containment",
    result: "nillycant",
  },
  {
    id: "craft.keeper.sights.sensations_winter_liquid.solomon_nillycant",
    reqs: {
      ability: 1,
      "s.sights.sensations": 1,
      "liquid.solomon": 1,
      winter: 15,
    },
    label: "Craft: Nillycant",
    duration: 120,
    skill: "s.sights.sensations",
    result: "nillycant",
  },
  {
    id: "craft.keeper.snowstories_winter_essential.periost_year-tally",
    reqs: {
      ability: 1,
      "s.snowstories": 1,
      "essential.periost": 1,
      winter: 15,
    },
    label: "Craft: Year-Tally",
    duration: 120,
    skill: "s.snowstories",
    result: "yeartally",
  },
  {
    id: "craft.keeper.raggedcrossroads_winter_essential.periost_year-tally",
    reqs: {
      ability: 1,
      "s.raggedcrossroads": 1,
      "essential.periost": 1,
      winter: 15,
    },
    label: "Craft: Year-Tally",
    duration: 120,
    skill: "s.raggedcrossroads",
    result: "yeartally",
  },
  {
    id: "craft.keeper.rhyme.remembrance_winter_essential.periost_year-tally",
    reqs: {
      ability: 1,
      "s.rhyme.remembrance": 1,
      "essential.periost": 1,
      winter: 15,
    },
    label: "Craft: Year-Tally",
    duration: 120,
    skill: "s.rhyme.remembrance",
    result: "yeartally",
  },
  {
    id: "craft.keeper.ouranoscopy_moon_asimel_didumos",
    reqs: {
      ability: 1,
      "s.ouranoscopy": 1,
      asimel: 1,
      moon: 15,
    },
    label: "Craft: Didumos",
    duration: 120,
    skill: "s.ouranoscopy",
    result: "didumos",
  },
  {
    id: "craft.keeper.putrefactions.calcinations_winter_essential.periost_year-tally",
    reqs: {
      ability: 1,
      "s.putrefactions.calcinations": 1,
      "essential.periost": 1,
      winter: 15,
    },
    label: "Craft: Year-Tally",
    duration: 120,
    skill: "s.putrefactions.calcinations",
    result: "yeartally",
  },
  {
    id: "craft.keeper.raggedcrossroads_winter_toxin.glassfinger_nillycant",
    reqs: {
      ability: 1,
      "s.raggedcrossroads": 1,
      "toxin.glassfinger": 1,
      winter: 15,
    },
    label: "Craft: Nillycant",
    duration: 120,
    skill: "s.raggedcrossroads",
    result: "nillycant",
  },
  {
    id: "craft.keeper.stitching.binding_winter_fabric_wyrd-weft",
    reqs: {
      ability: 1,
      "s.stitching.binding": 1,
      fabric: 1,
      winter: 15,
    },
    label: "Craft: Wyrd-Weft",
    duration: 120,
    skill: "s.stitching.binding",
    result: "wyrdweft",
  },
  {
    id: "craft.keeper.weaving.knotworking_winter_fabric_wyrd-weft",
    reqs: {
      ability: 1,
      "s.weaving.knotworking": 1,
      fabric: 1,
      winter: 15,
    },
    label: "Craft: Wyrd-Weft",
    duration: 120,
    skill: "s.weaving.knotworking",
    result: "wyrdweft",
  },
  {
    id: "craft.keeper.glaziery.lightsmithing_lantern_essence.xanthotic_uzult",
    reqs: {
      ability: 1,
      "s.glaziery.lightsmithing": 1,
      "essence.xanthotic": 1,
      lantern: 15,
    },
    label: "Craft: Uzult",
    duration: 120,
    skill: "s.glaziery.lightsmithing",
    result: "uzult",
  },
  {
    id: "craft.keeper.transformations.liberations_forge_salts.bitterblack_iotic essence",
    reqs: {
      ability: 1,
      "s.transformations.liberations": 1,
      "salts.bitterblack": 1,
      forge: 15,
    },
    label: "Craft: Iotic Essence",
    duration: 120,
    skill: "s.transformations.liberations",
    result: "essence.iotic",
  },
  {
    id: "craft.keeper.meniscatereflections_forge_salts.bitterblack_iotic essence",
    reqs: {
      ability: 1,
      "s.meniscatereflections": 1,
      "salts.bitterblack": 1,
      forge: 15,
    },
    label: "Craft: Iotic Essence",
    duration: 120,
    skill: "s.meniscatereflections",
    result: "essence.iotic",
  },
  {
    id: "craft.keeper.disciplines.thehammer_forge_salts.bitterblack_iotic essence",
    reqs: {
      ability: 1,
      "s.disciplines.thehammer": 1,
      "salts.bitterblack": 1,
      forge: 15,
    },
    label: "Craft: Iotic Essence",
    duration: 120,
    skill: "s.disciplines.thehammer",
    result: "essence.iotic",
  },
  {
    id: "craft.keeper.purifications.exaltations_lantern_salts.bitterblack_xanthotic essence",
    reqs: {
      ability: 1,
      "s.purifications.exaltations": 1,
      "salts.bitterblack": 1,
      lantern: 15,
    },
    label: "Craft: Xanthotic Essence",
    duration: 120,
    skill: "s.purifications.exaltations",
    result: "essence.xanthotic",
  },
  {
    id: "craft.keeper.transformations.liberations_forge_essence.xanthotic_iotic essence",
    reqs: {
      ability: 1,
      "s.transformations.liberations": 1,
      "essence.xanthotic": 1,
      forge: 15,
    },
    label: "Craft: Iotic Essence",
    duration: 120,
    skill: "s.transformations.liberations",
    result: "essence.iotic",
  },
  {
    id: "craft.keeper.meniscatereflections_forge_essence.xanthotic_iotic essence",
    reqs: {
      ability: 1,
      "s.meniscatereflections": 1,
      "essence.xanthotic": 1,
      forge: 15,
    },
    label: "Craft: Iotic Essence",
    duration: 120,
    skill: "s.meniscatereflections",
    result: "essence.iotic",
  },
  {
    id: "craft.keeper.disciplines.thehammer_forge_essence.xanthotic_iotic essence",
    reqs: {
      ability: 1,
      "s.disciplines.thehammer": 1,
      "essence.xanthotic": 1,
      forge: 15,
    },
    label: "Craft: Iotic Essence",
    duration: 120,
    skill: "s.disciplines.thehammer",
    result: "essence.iotic",
  },
  {
    id: "craft.keeper.glassblowing.vesselcrafting_sky_ichor.vitreous_ashartine",
    reqs: {
      ability: 1,
      "s.glassblowing.vesselcrafting": 1,
      "ichor.vitreous": 1,
      sky: 15,
    },
    label: "Craft: Ashartine",
    duration: 120,
    skill: "s.glassblowing.vesselcrafting",
    result: "ashartine",
  },
  {
    id: "craft.keeper.glassblowing.vesselcrafting_lantern_ichor.vitreous_ashartine",
    reqs: {
      ability: 1,
      "s.glassblowing.vesselcrafting": 1,
      "ichor.vitreous": 1,
      lantern: 15,
    },
    label: "Craft: Ashartine",
    duration: 120,
    skill: "s.glassblowing.vesselcrafting",
    result: "ashartine",
  },
  {
    id: "craft.keeper.solutions.separations_moth_honey.cuckoo_skinshuck mead",
    reqs: {
      ability: 1,
      "s.solutions.separations": 1,
      "honey.cuckoo": 1,
      moth: 15,
    },
    label: "Craft: Skinshuck Mead",
    duration: 120,
    skill: "s.solutions.separations",
    result: "skinshuck.mead",
  },
  {
    id: "craft.keeper.edictsliminal_moth_honey.cuckoo_skinshuck mead",
    reqs: {
      ability: 1,
      "s.edictsliminal": 1,
      "honey.cuckoo": 1,
      moth: 15,
    },
    label: "Craft: Skinshuck Mead",
    duration: 120,
    skill: "s.edictsliminal",
    result: "skinshuck.mead",
  },
  {
    id: "craft.keeper.sacralimiae_moth_honey.cuckoo_skinshuck mead",
    reqs: {
      ability: 1,
      "s.sacralimiae": 1,
      "honey.cuckoo": 1,
      moth: 15,
    },
    label: "Craft: Skinshuck Mead",
    duration: 120,
    skill: "s.sacralimiae",
    result: "skinshuck.mead",
  },
  {
    id: "craft.keeper.rites.theroots_moth_honey.cuckoo_skinshuck mead",
    reqs: {
      ability: 1,
      "s.rites.theroots": 1,
      "honey.cuckoo": 1,
      moth: 15,
    },
    label: "Craft: Skinshuck Mead",
    duration: 120,
    skill: "s.rites.theroots",
    result: "skinshuck.mead",
  },
  {
    id: "craft.keeper.insects.nectars_moon_honey.cuckoo_skinshuck mead",
    reqs: {
      ability: 1,
      "s.insects.nectars": 1,
      "honey.cuckoo": 1,
      moon: 15,
    },
    label: "Craft: Skinshuck Mead",
    duration: 120,
    skill: "s.insects.nectars",
    result: "skinshuck.mead",
  },
  {
    id: "craft.keeper.resurgences.emergences_moth_honey.cuckoo_skinshuck mead",
    reqs: {
      ability: 1,
      "s.resurgences.emergences": 1,
      "honey.cuckoo": 1,
      moth: 15,
    },
    label: "Craft: Skinshuck Mead",
    duration: 120,
    skill: "s.resurgences.emergences",
    result: "skinshuck.mead",
  },
  {
    id: "craft.keeper.transformations.liberations_moth_honey.cuckoo_skinshuck mead",
    reqs: {
      ability: 1,
      "s.transformations.liberations": 1,
      "honey.cuckoo": 1,
      moth: 15,
    },
    label: "Craft: Skinshuck Mead",
    duration: 120,
    skill: "s.transformations.liberations",
    result: "skinshuck.mead",
  },
  {
    id: "craft.keeper.glaziery.lightsmithing_knock_gervinite_chronsichord",
    reqs: {
      ability: 1,
      "s.glaziery.lightsmithing": 1,
      gervinite: 1,
      knock: 15,
    },
    label: "Craft: Chronsichord",
    duration: 120,
    skill: "s.glaziery.lightsmithing",
    result: "chronsicord",
  },
  {
    id: "craft.keeper.anbary.lapidary_lantern_essence.xanthotic_uzult",
    reqs: {
      ability: 1,
      "s.anbary.lapidary": 1,
      "essence.xanthotic": 1,
      lantern: 15,
    },
    label: "Craft: Uzult",
    duration: 120,
    skill: "s.anbary.lapidary",
    result: "uzult",
  },
  {
    id: "craft.keeper.inks.power_rose_ichor.auroral_porphyrine",
    reqs: {
      ability: 1,
      "s.inks.power": 1,
      "ichor.auroral": 1,
      rose: 15,
    },
    label: "Craft: Porphyrine",
    duration: 120,
    skill: "s.inks.power",
    result: "porphyrine",
  },
  {
    id: "craft.keeper.ouranoscopy_sky_essential.periost_year-tally",
    reqs: {
      ability: 1,
      "s.ouranoscopy": 1,
      "essential.periost": 1,
      sky: 15,
    },
    label: "Craft: Year-Tally",
    duration: 120,
    skill: "s.ouranoscopy",
    result: "yeartally",
  },
  {
    id: "craft.keeper.stonestories_nectar_essential.periost_sacrament malachite",
    reqs: {
      ability: 1,
      "s.stonestories": 1,
      "essential.periost": 1,
      nectar: 15,
    },
    label: "Craft: Sacrament Malachite",
    duration: 120,
    skill: "s.stonestories",
    result: "sacrament.malachite",
  },
  {
    id: "craft.keeper.insects.nectars_moth_larva.chimeric_perilous imago",
    reqs: {
      ability: 1,
      "s.insects.nectars": 1,
      "larva.chimeric": 1,
      moth: 15,
    },
    label: "Craft: Perilous Imago",
    duration: 120,
    skill: "s.insects.nectars",
    result: "imago.perilous",
  },
  {
    id: "craft.scholar.disciplines.thescar_leaf_perinculate_edge",
    reqs: {
      ability: 1,
      "s.disciplines.thescar": 1,
      leaf: 1,
      edge: 10,
    },
    label: "Craft: Perinculate",
    duration: 90,
    skill: "s.disciplines.thescar",
    result: "perinculate",
  },
  {
    id: "craft.scholar.edictsmartial_leaf_perinculate_edge",
    reqs: {
      ability: 1,
      "s.edictsmartial": 1,
      leaf: 1,
      edge: 10,
    },
    label: "Craft: Perinculate",
    duration: 90,
    skill: "s.edictsmartial",
    result: "perinculate",
  },
  {
    id: "craft.scholar.inks.power_leaf_perinculate_scale",
    reqs: {
      ability: 1,
      "s.inks.power": 1,
      leaf: 1,
      scale: 10,
    },
    label: "Craft: Perinculate",
    duration: 90,
    skill: "s.inks.power",
    result: "perinculate",
  },
  {
    id: "craft.scholar.sickle.eclipse_leaf_perinculate_edge",
    reqs: {
      ability: 1,
      "s.sickle.eclipse": 1,
      leaf: 1,
      edge: 10,
    },
    label: "Craft: Perinculate",
    duration: 90,
    skill: "s.sickle.eclipse",
    result: "perinculate",
  },
  {
    id: "craft.scholar.putrefactions.calcinations_leaf_perinculate_winter",
    reqs: {
      ability: 1,
      "s.putrefactions.calcinations": 1,
      leaf: 1,
      winter: 10,
    },
    label: "Craft: Perinculate",
    duration: 90,
    skill: "s.putrefactions.calcinations",
    result: "perinculate",
  },
  {
    id: "craft.scholar.disciplines.thehammer_memory_winning.move_edge",
    reqs: {
      ability: 1,
      "s.disciplines.thehammer": 1,
      memory: 1,
      edge: 10,
    },
    label: "Craft: Winning Move",
    duration: 90,
    skill: "s.disciplines.thehammer",
    result: "winning.move",
  },
  {
    id: "craft.scholar.auroralcontemplations_memory_winning.move_edge",
    reqs: {
      ability: 1,
      "s.auroralcontemplations": 1,
      memory: 1,
      edge: 10,
    },
    label: "Craft: Winning Move",
    duration: 90,
    skill: "s.auroralcontemplations",
    result: "winning.move",
  },
  {
    id: "craft.scholar.meniscatereflections_memory_winning.move_edge",
    reqs: {
      ability: 1,
      "s.meniscatereflections": 1,
      memory: 1,
      edge: 10,
    },
    label: "Craft: Winning Move",
    duration: 90,
    skill: "s.meniscatereflections",
    result: "winning.move",
  },
  {
    id: "craft.scholar.sharps_memory_winning.move_edge",
    reqs: {
      ability: 1,
      "s.sharps": 1,
      memory: 1,
      edge: 10,
    },
    label: "Craft: Winning Move",
    duration: 90,
    skill: "s.sharps",
    result: "winning.move",
  },
  {
    id: "craft.scholar.raggedcrossroads_memory_winning.move_edge",
    reqs: {
      ability: 1,
      "s.raggedcrossroads": 1,
      memory: 1,
      edge: 10,
    },
    label: "Craft: Winning Move",
    duration: 90,
    skill: "s.raggedcrossroads",
    result: "winning.move",
  },
  {
    id: "craft.scholar.transformations.liberations_liquid_essence.iotic_forge",
    reqs: {
      ability: 1,
      "s.transformations.liberations": 1,
      liquid: 1,
      forge: 10,
    },
    label: "Craft: Iotic Essence",
    duration: 90,
    skill: "s.transformations.liberations",
    result: "essence.iotic",
  },
  {
    id: "craft.scholar.anbary.lapidary_liquid_essence.iotic_forge",
    reqs: {
      ability: 1,
      "s.anbary.lapidary": 1,
      liquid: 1,
      forge: 10,
    },
    label: "Craft: Iotic Essence",
    duration: 90,
    skill: "s.anbary.lapidary",
    result: "essence.iotic",
  },
  {
    id: "craft.scholar.pyroglyphics_liquid_essence.iotic_forge",
    reqs: {
      ability: 1,
      "s.pyroglyphics": 1,
      liquid: 1,
      forge: 10,
    },
    label: "Craft: Iotic Essence",
    duration: 90,
    skill: "s.pyroglyphics",
    result: "essence.iotic",
  },
  {
    id: "craft.scholar.spices.savours_liquid_essence.iotic_forge",
    reqs: {
      ability: 1,
      "s.spices.savours": 1,
      liquid: 1,
      forge: 10,
    },
    label: "Craft: Iotic Essence",
    duration: 90,
    skill: "s.spices.savours",
    result: "essence.iotic",
  },
  {
    id: "craft.scholar.meniscatereflections_liquid_essence.iotic_forge",
    reqs: {
      ability: 1,
      "s.meniscatereflections": 1,
      liquid: 1,
      forge: 10,
    },
    label: "Craft: Iotic Essence",
    duration: 90,
    skill: "s.meniscatereflections",
    result: "essence.iotic",
  },
  {
    id: "craft.scholar.bells.brazieries_metal_wire.orichalcum_forge",
    reqs: {
      ability: 1,
      "s.bells.brazieries": 1,
      metal: 1,
      forge: 10,
    },
    label: "Craft: Wire (Orichalcum)",
    duration: 90,
    skill: "s.bells.brazieries",
    result: "wire.orichalcum",
  },
  {
    id: "craft.scholar.door.wall_metal_wire.orichalcum_forge",
    reqs: {
      ability: 1,
      "s.door.wall": 1,
      metal: 1,
      forge: 10,
    },
    label: "Craft: Wire (Orichalcum)",
    duration: 90,
    skill: "s.door.wall",
    result: "wire.orichalcum",
  },
  {
    id: "craft.scholar.disciplines.thehammer_metal_wire.orichalcum_forge",
    reqs: {
      ability: 1,
      "s.disciplines.thehammer": 1,
      metal: 1,
      forge: 10,
    },
    label: "Craft: Wire (Orichalcum)",
    duration: 90,
    skill: "s.disciplines.thehammer",
    result: "wire.orichalcum",
  },
  {
    id: "craft.scholar.glassblowing.vesselcrafting_metal_wire.orichalcum_knock",
    reqs: {
      ability: 1,
      "s.glassblowing.vesselcrafting": 1,
      metal: 1,
      knock: 10,
    },
    label: "Craft: Wire (Orichalcum)",
    duration: 90,
    skill: "s.glassblowing.vesselcrafting",
    result: "wire.orichalcum",
  },
  {
    id: "craft.scholar.lockworks.clockworks_metal_wire.orichalcum_sky",
    reqs: {
      ability: 1,
      "s.lockworks.clockworks": 1,
      metal: 1,
      sky: 10,
    },
    label: "Craft: Wire (Orichalcum)",
    duration: 90,
    skill: "s.lockworks.clockworks",
    result: "wire.orichalcum",
  },
  {
    id: "craft.scholar.desires.dissolutions_flower_rubywise.ruin_grail",
    reqs: {
      ability: 1,
      "s.desires.dissolutions": 1,
      flower: 1,
      grail: 10,
    },
    label: "Craft: Rubywise Ruin",
    duration: 90,
    skill: "s.desires.dissolutions",
    result: "rubywise.ruin",
  },
  {
    id: "craft.scholar.orchids.narcotics_flower_rubywise.ruin_grail",
    reqs: {
      ability: 1,
      "s.orchids.narcotics": 1,
      flower: 1,
      grail: 10,
    },
    label: "Craft: Rubywise Ruin",
    duration: 90,
    skill: "s.orchids.narcotics",
    result: "rubywise.ruin",
  },
  {
    id: "craft.scholar.applebrighteuphonies_flower_rubywise.ruin_grail",
    reqs: {
      ability: 1,
      "s.applebrighteuphonies": 1,
      flower: 1,
      grail: 10,
    },
    label: "Craft: Rubywise Ruin",
    duration: 90,
    skill: "s.applebrighteuphonies",
    result: "rubywise.ruin",
  },
  {
    id: "craft.scholar.leaves.thorns_flower_rubywise.ruin_grail",
    reqs: {
      ability: 1,
      "s.leaves.thorns": 1,
      flower: 1,
      grail: 10,
    },
    label: "Craft: Rubywise Ruin",
    duration: 90,
    skill: "s.leaves.thorns",
    result: "rubywise.ruin",
  },
  {
    id: "craft.scholar.seastories_fabric_pale.mommet_grail",
    reqs: {
      ability: 1,
      "s.seastories": 1,
      fabric: 1,
      grail: 10,
    },
    label: "Craft: Pale Mommet",
    duration: 90,
    skill: "s.seastories",
    result: "pale.mommet",
  },
  {
    id: "craft.scholar.pentiments.precursors_fabric_pale.mommet_grail",
    reqs: {
      ability: 1,
      "s.pentiments.precursors": 1,
      fabric: 1,
      grail: 10,
    },
    label: "Craft: Pale Mommet",
    duration: 90,
    skill: "s.pentiments.precursors",
    result: "pale.mommet",
  },
  {
    id: "craft.scholar.resurgences.emergences_fabric_pale.mommet_grail",
    reqs: {
      ability: 1,
      "s.resurgences.emergences": 1,
      fabric: 1,
      grail: 10,
    },
    label: "Craft: Pale Mommet",
    duration: 90,
    skill: "s.resurgences.emergences",
    result: "pale.mommet",
  },
  {
    id: "craft.scholar.thegreatsignsandthegreatscars_fabric_pale.mommet_grail",
    reqs: {
      ability: 1,
      "s.thegreatsignsandthegreatscars": 1,
      fabric: 1,
      grail: 10,
    },
    label: "Craft: Pale Mommet",
    duration: 90,
    skill: "s.thegreatsignsandthegreatscars",
    result: "pale.mommet",
  },
  {
    id: "craft.scholar.tridesmahiera_fabric_pale.mommet_grail",
    reqs: {
      ability: 1,
      "s.tridesmahiera": 1,
      fabric: 1,
      grail: 10,
    },
    label: "Craft: Pale Mommet",
    duration: 90,
    skill: "s.tridesmahiera",
    result: "pale.mommet",
  },
  {
    id: "craft.scholar.weaving.knotworking_fabric_frithweft_heart",
    reqs: {
      ability: 1,
      "s.weaving.knotworking": 1,
      fabric: 1,
      heart: 10,
    },
    label: "Craft: Frith-Weft",
    duration: 90,
    skill: "s.weaving.knotworking",
    result: "frithweft",
  },
  {
    id: "craft.scholar.stitching.binding_fabric_frithweft_heart",
    reqs: {
      ability: 1,
      "s.stitching.binding": 1,
      fabric: 1,
      heart: 10,
    },
    label: "Craft: Frith-Weft",
    duration: 90,
    skill: "s.stitching.binding",
    result: "frithweft",
  },
  {
    id: "craft.scholar.quenchings.quellings_liquid_liquid.gideon_heart",
    reqs: {
      ability: 1,
      "s.quenchings.quellings": 1,
      liquid: 1,
      heart: 10,
    },
    label: "Craft: Gideon's Soaks",
    duration: 90,
    skill: "s.quenchings.quellings",
    result: "liquid.gideon",
  },
  {
    id: "craft.scholar.edictsinviolable_liquid_liquid.gideon_heart",
    reqs: {
      ability: 1,
      "s.edictsinviolable": 1,
      liquid: 1,
      heart: 10,
    },
    label: "Craft: Gideon's Soaks",
    duration: 90,
    skill: "s.edictsinviolable",
    result: "liquid.gideon",
  },
  {
    id: "craft.scholar.drums.dances_instrument_music.thunderskin_heart",
    reqs: {
      ability: 1,
      "s.drums.dances": 1,
      instrument: 1,
      heart: 10,
    },
    label: "Craft: Thunderskin's Paean",
    duration: 90,
    skill: "s.drums.dances",
    result: "music.thunderskin",
  },
  {
    id: "craft.scholar.surgeries.exsanguinations_liquid_liquid.gideon_heart",
    reqs: {
      ability: 1,
      "s.surgeries.exsanguinations": 1,
      liquid: 1,
      heart: 10,
    },
    label: "Craft: Gideon's Soaks",
    duration: 90,
    skill: "s.surgeries.exsanguinations",
    result: "liquid.gideon",
  },
  {
    id: "craft.scholar.maggephenemysteries_liquid_liquid.gideon_heart",
    reqs: {
      ability: 1,
      "s.maggephenemysteries": 1,
      liquid: 1,
      heart: 10,
    },
    label: "Craft: Gideon's Soaks",
    duration: 90,
    skill: "s.maggephenemysteries",
    result: "liquid.gideon",
  },
  {
    id: "craft.scholar.herbs.infusions_liquid_liquid.gideon_heart",
    reqs: {
      ability: 1,
      "s.herbs.infusions": 1,
      liquid: 1,
      heart: 10,
    },
    label: "Craft: Gideon's Soaks",
    duration: 90,
    skill: "s.herbs.infusions",
    result: "liquid.gideon",
  },
  {
    id: "craft.scholar.lockworks.clockworks_glass_gervinite_knock",
    reqs: {
      ability: 1,
      "s.lockworks.clockworks": 1,
      glass: 1,
      knock: 10,
    },
    label: "Craft: Gervinite",
    duration: 90,
    skill: "s.lockworks.clockworks",
    result: "gervinite",
  },
  {
    id: "craft.scholar.thegreatsignsandthegreatscars_memory_curious.hunch_knock",
    reqs: {
      ability: 1,
      "s.thegreatsignsandthegreatscars": 1,
      memory: 1,
      knock: 10,
    },
    label: "Craft: Curious Hunch",
    duration: 90,
    skill: "s.thegreatsignsandthegreatscars",
    result: "curious.hunch",
  },
  {
    id: "craft.scholar.edictsliminal_liquid_toxin.glassfinger_knock",
    reqs: {
      ability: 1,
      "s.edictsliminal": 1,
      liquid: 1,
      knock: 10,
    },
    label: "Craft: Glassfinger Toxin",
    duration: 90,
    skill: "s.edictsliminal",
    result: "toxin.glassfinger",
  },
  {
    id: "craft.scholar.preliminalmeter_memory_curious.hunch_knock",
    reqs: {
      ability: 1,
      "s.preliminalmeter": 1,
      memory: 1,
      knock: 10,
    },
    label: "Craft: Curious Hunch",
    duration: 90,
    skill: "s.preliminalmeter",
    result: "curious.hunch",
  },
  {
    id: "craft.scholar.glaziery.lightsmithing_metal_gervinite_sky",
    reqs: {
      ability: 1,
      "s.glaziery.lightsmithing": 1,
      metal: 1,
      sky: 10,
    },
    label: "Craft: Gervinite",
    duration: 90,
    skill: "s.glaziery.lightsmithing",
    result: "gervinite",
  },
  {
    id: "craft.scholar.solutions.separations_liquid_toxin.glassfinger_knock",
    reqs: {
      ability: 1,
      "s.solutions.separations": 1,
      liquid: 1,
      knock: 10,
    },
    label: "Craft: Glassfinger Toxin",
    duration: 90,
    skill: "s.solutions.separations",
    result: "toxin.glassfinger",
  },
  {
    id: "craft.scholar.serpents.venoms_liquid_toxin.glassfinger_scale",
    reqs: {
      ability: 1,
      "s.serpents.venoms": 1,
      liquid: 1,
      scale: 10,
    },
    label: "Craft: Glassfinger Toxin",
    duration: 90,
    skill: "s.serpents.venoms",
    result: "toxin.glassfinger",
  },
  {
    id: "craft.scholar.surgeries.exsanguinations_liquid_toxin.glassfinger_knock",
    reqs: {
      ability: 1,
      "s.surgeries.exsanguinations": 1,
      liquid: 1,
      knock: 10,
    },
    label: "Craft: Glassfinger Toxin",
    duration: 90,
    skill: "s.surgeries.exsanguinations",
    result: "toxin.glassfinger",
  },
  {
    id: "craft.scholar.stonestories_remains_essential.periost_nectar",
    reqs: {
      ability: 1,
      "s.stonestories": 1,
      remains: 1,
      nectar: 10,
    },
    label: "Craft: Essential Periost",
    duration: 90,
    skill: "s.stonestories",
    result: "essential.periost",
  },
  {
    id: "craft.scholar.door.wall_liquid_toxin.glassfinger_knock",
    reqs: {
      ability: 1,
      "s.door.wall": 1,
      liquid: 1,
      knock: 10,
    },
    label: "Craft: Glassfinger Toxin",
    duration: 90,
    skill: "s.door.wall",
    result: "toxin.glassfinger",
  },
  {
    id: "craft.scholar.purifications.exaltations_liquid_essence.xanthotic_lantern",
    reqs: {
      ability: 1,
      "s.purifications.exaltations": 1,
      liquid: 1,
      lantern: 10,
    },
    label: "Craft: Xanthotic Essence",
    duration: 90,
    skill: "s.purifications.exaltations",
    result: "essence.xanthotic",
  },
  {
    id: "craft.scholar.transformations.liberations_liquid_essence.xanthotic_lantern",
    reqs: {
      ability: 1,
      "s.transformations.liberations": 1,
      liquid: 1,
      lantern: 10,
    },
    label: "Craft: Xanthotic Essence",
    duration: 90,
    skill: "s.transformations.liberations",
    result: "essence.xanthotic",
  },
  {
    id: "craft.scholar.auroralcontemplations_liquid_essence.xanthotic_lantern",
    reqs: {
      ability: 1,
      "s.auroralcontemplations": 1,
      liquid: 1,
      lantern: 10,
    },
    label: "Craft: Xanthotic Essence",
    duration: 90,
    skill: "s.auroralcontemplations",
    result: "essence.xanthotic",
  },
  {
    id: "craft.scholar.pyroglyphics_liquid_essence.xanthotic_lantern",
    reqs: {
      ability: 1,
      "s.pyroglyphics": 1,
      liquid: 1,
      lantern: 10,
    },
    label: "Craft: Xanthotic Essence",
    duration: 90,
    skill: "s.pyroglyphics",
    result: "essence.xanthotic",
  },
  {
    id: "craft.scholar.anbary.lapidary_liquid_essence.xanthotic_sky",
    reqs: {
      ability: 1,
      "s.anbary.lapidary": 1,
      liquid: 1,
      sky: 10,
    },
    label: "Craft: Xanthotic Essence",
    duration: 90,
    skill: "s.anbary.lapidary",
    result: "essence.xanthotic",
  },
  {
    id: "craft.scholar.glassblowing.vesselcrafting_glass_ichor.vitreous_lantern",
    reqs: {
      ability: 1,
      "s.glassblowing.vesselcrafting": 1,
      glass: 1,
      lantern: 10,
    },
    label: "Craft: Ichor Vitreous",
    duration: 90,
    skill: "s.glassblowing.vesselcrafting",
    result: "ichor.vitreous",
  },
  {
    id: "craft.scholar.watchmansparadoxes_glass_ichor.vitreous_lantern",
    reqs: {
      ability: 1,
      "s.watchmansparadoxes": 1,
      glass: 1,
      lantern: 10,
    },
    label: "Craft: Ichor Vitreous",
    duration: 90,
    skill: "s.watchmansparadoxes",
    result: "ichor.vitreous",
  },
  {
    id: "craft.scholar.glaziery.lightsmithing_glass_ichor.vitreous_lantern",
    reqs: {
      ability: 1,
      "s.glaziery.lightsmithing": 1,
      glass: 1,
      lantern: 10,
    },
    label: "Craft: Ichor Vitreous",
    duration: 90,
    skill: "s.glaziery.lightsmithing",
    result: "ichor.vitreous",
  },
  {
    id: "craft.scholar.sacrasolisinvicti_glass_ichor.vitreous_lantern",
    reqs: {
      ability: 1,
      "s.sacrasolisinvicti": 1,
      glass: 1,
      lantern: 10,
    },
    label: "Craft: Ichor Vitreous",
    duration: 90,
    skill: "s.sacrasolisinvicti",
    result: "ichor.vitreous",
  },
  {
    id: "craft.scholar.inks.revelation_glass_ichor.vitreous_lantern",
    reqs: {
      ability: 1,
      "s.inks.revelation": 1,
      glass: 1,
      lantern: 10,
    },
    label: "Craft: Ichor Vitreous",
    duration: 90,
    skill: "s.inks.revelation",
    result: "ichor.vitreous",
  },
  {
    id: "craft.scholar.rhyme.remembrance_egg_stymphling_moon",
    reqs: {
      ability: 1,
      "s.rhyme.remembrance": 1,
      egg: 1,
      moon: 10,
    },
    label: "Craft: Stymphling",
    duration: 90,
    skill: "s.rhyme.remembrance",
    result: "stymphling",
  },
  {
    id: "craft.scholar.sandstories_egg_stymphling_moon",
    reqs: {
      ability: 1,
      "s.sandstories": 1,
      egg: 1,
      moon: 10,
    },
    label: "Craft: Stymphling",
    duration: 90,
    skill: "s.sandstories",
    result: "stymphling",
  },
  {
    id: "craft.scholar.tridesmahiera_egg_stymphling_moon",
    reqs: {
      ability: 1,
      "s.tridesmahiera": 1,
      egg: 1,
      moon: 10,
    },
    label: "Craft: Stymphling",
    duration: 90,
    skill: "s.tridesmahiera",
    result: "stymphling",
  },
  {
    id: "craft.scholar.snowstories_egg_stymphling_moon",
    reqs: {
      ability: 1,
      "s.snowstories": 1,
      egg: 1,
      moon: 10,
    },
    label: "Craft: Stymphling",
    duration: 90,
    skill: "s.snowstories",
    result: "stymphling",
  },
  {
    id: "craft.scholar.serpents.venoms_egg_stymphling_moon",
    reqs: {
      ability: 1,
      "s.serpents.venoms": 1,
      egg: 1,
      moon: 10,
    },
    label: "Craft: Stymphling",
    duration: 90,
    skill: "s.serpents.venoms",
    result: "stymphling",
  },
  {
    id: "craft.scholar.inks.revelation_liquid_asimel_moth",
    reqs: {
      ability: 1,
      "s.inks.revelation": 1,
      liquid: 1,
      moth: 10,
    },
    label: "Craft: Asimel",
    duration: 90,
    skill: "s.inks.revelation",
    result: "asimel",
  },
  {
    id: "craft.scholar.putrefactions.calcinations_liquid_asimel_moon",
    reqs: {
      ability: 1,
      "s.putrefactions.calcinations": 1,
      liquid: 1,
      moon: 10,
    },
    label: "Craft: Asimel",
    duration: 90,
    skill: "s.putrefactions.calcinations",
    result: "asimel",
  },
  {
    id: "craft.scholar.seastories_liquid_asimel_moon",
    reqs: {
      ability: 1,
      "s.seastories": 1,
      liquid: 1,
      moon: 10,
    },
    label: "Craft: Asimel",
    duration: 90,
    skill: "s.seastories",
    result: "asimel",
  },
  {
    id: "craft.scholar.ouranoscopy_lens_asimel_moon",
    reqs: {
      ability: 1,
      "s.ouranoscopy": 1,
      lens: 1,
      moon: 10,
    },
    label: "Craft: Asimel",
    duration: 90,
    skill: "s.ouranoscopy",
    result: "asimel",
  },
  {
    id: "craft.scholar.pearl.tide_liquid_asimel_moon",
    reqs: {
      ability: 1,
      "s.pearl.tide": 1,
      liquid: 1,
      moon: 10,
    },
    label: "Craft: Asimel",
    duration: 90,
    skill: "s.pearl.tide",
    result: "asimel",
  },
  {
    id: "craft.scholar.sacralimiae_memory_riddle.nameday_moth",
    reqs: {
      ability: 1,
      "s.sacralimiae": 1,
      memory: 1,
      moth: 10,
    },
    label: "Craft: Nameday Riddle",
    duration: 90,
    skill: "s.sacralimiae",
    result: "riddle.nameday",
  },
  {
    id: "craft.scholar.edictsliminal_memory_riddle.nameday_moth",
    reqs: {
      ability: 1,
      "s.edictsliminal": 1,
      memory: 1,
      moth: 10,
    },
    label: "Craft: Nameday Riddle",
    duration: 90,
    skill: "s.edictsliminal",
    result: "riddle.nameday",
  },
  {
    id: "craft.scholar.solutions.separations_memory_riddle.nameday_moth",
    reqs: {
      ability: 1,
      "s.solutions.separations": 1,
      memory: 1,
      moth: 10,
    },
    label: "Craft: Nameday Riddle",
    duration: 90,
    skill: "s.solutions.separations",
    result: "riddle.nameday",
  },
  {
    id: "craft.scholar.rites.theroots_memory_riddle.nameday_moth",
    reqs: {
      ability: 1,
      "s.rites.theroots": 1,
      memory: 1,
      moth: 10,
    },
    label: "Craft: Nameday Riddle",
    duration: 90,
    skill: "s.rites.theroots",
    result: "riddle.nameday",
  },
  {
    id: "craft.scholar.weaving.knotworking_memory_riddle.nameday_moth",
    reqs: {
      ability: 1,
      "s.weaving.knotworking": 1,
      memory: 1,
      moth: 10,
    },
    label: "Craft: Nameday Riddle",
    duration: 90,
    skill: "s.weaving.knotworking",
    result: "riddle.nameday",
  },
  {
    id: "craft.scholar.insects.nectars_egg_larva.chimeric_moon",
    reqs: {
      ability: 1,
      "s.insects.nectars": 1,
      egg: 1,
      moon: 10,
    },
    label: "Craft: Chimeric Larva",
    duration: 90,
    skill: "s.insects.nectars",
    result: "larva.chimeric",
  },
  {
    id: "craft.scholar.resurgences.emergences_egg_larva.chimeric_moth",
    reqs: {
      ability: 1,
      "s.resurgences.emergences": 1,
      egg: 1,
      moth: 10,
    },
    label: "Craft: Chimeric Larva",
    duration: 90,
    skill: "s.resurgences.emergences",
    result: "larva.chimeric",
  },
  {
    id: "craft.scholar.transformations.liberations_egg_larva.chimeric_moth",
    reqs: {
      ability: 1,
      "s.transformations.liberations": 1,
      egg: 1,
      moth: 10,
    },
    label: "Craft: Chimeric Larva",
    duration: 90,
    skill: "s.transformations.liberations",
    result: "larva.chimeric",
  },
  {
    id: "craft.scholar.horns.ivories_egg_larva.chimeric_knock",
    reqs: {
      ability: 1,
      "s.horns.ivories": 1,
      egg: 1,
      knock: 10,
    },
    label: "Craft: Chimeric Larva",
    duration: 90,
    skill: "s.horns.ivories",
    result: "larva.chimeric",
  },
  {
    id: "craft.scholar.hill.hollow_egg_larva.chimeric_moon",
    reqs: {
      ability: 1,
      "s.hill.hollow": 1,
      egg: 1,
      moon: 10,
    },
    label: "Craft: Chimeric Larva",
    duration: 90,
    skill: "s.hill.hollow",
    result: "larva.chimeric",
  },
  {
    id: "craft.scholar.leaves.thorns_wood_pyrus.auricalcinus_nectar",
    reqs: {
      ability: 1,
      "s.leaves.thorns": 1,
      wood: 1,
      nectar: 10,
    },
    label: "Craft: Pyrus Auricalcinus",
    duration: 90,
    skill: "s.leaves.thorns",
    result: "pyrus.auricalcinus",
  },
  {
    id: "craft.scholar.spices.savours_wood_pyrus.auricalcinus_nectar",
    reqs: {
      ability: 1,
      "s.spices.savours": 1,
      wood: 1,
      nectar: 10,
    },
    label: "Craft: Pyrus Auricalcinus",
    duration: 90,
    skill: "s.spices.savours",
    result: "pyrus.auricalcinus",
  },
  {
    id: "craft.scholar.drums.dances_instrument_music.hive_nectar",
    reqs: {
      ability: 1,
      "s.drums.dances": 1,
      instrument: 1,
      nectar: 10,
    },
    label: "Craft: Hive's Lament",
    duration: 90,
    skill: "s.drums.dances",
    result: "music.hive",
  },
  {
    id: "craft.scholar.coil.chasm_wood_pyrus.auricalcinus_nectar",
    reqs: {
      ability: 1,
      "s.coil.chasm": 1,
      wood: 1,
      nectar: 10,
    },
    label: "Craft: Pyrus Auricalcinus",
    duration: 90,
    skill: "s.coil.chasm",
    result: "pyrus.auricalcinus",
  },
  {
    id: "craft.scholar.desires.dissolutions_wood_pyrus.auricalcinus_moon",
    reqs: {
      ability: 1,
      "s.desires.dissolutions": 1,
      wood: 1,
      moon: 10,
    },
    label: "Craft: Pyrus Auricalcinus",
    duration: 90,
    skill: "s.desires.dissolutions",
    result: "pyrus.auricalcinus",
  },
  {
    id: "craft.scholar.inks.containment_beverage_leathy_moon",
    reqs: {
      ability: 1,
      "s.inks.containment": 1,
      beverage: 1,
      moon: 10,
    },
    label: "Craft: Leathy",
    duration: 90,
    skill: "s.inks.containment",
    result: "leathy",
  },
  {
    id: "craft.scholar.rites.theroots_beverage_leathy_nectar",
    reqs: {
      ability: 1,
      "s.rites.theroots": 1,
      beverage: 1,
      nectar: 10,
    },
    label: "Craft: Leathy",
    duration: 90,
    skill: "s.rites.theroots",
    result: "leathy",
  },
  {
    id: "craft.scholar.orchids.narcotics_beverage_leathy_nectar",
    reqs: {
      ability: 1,
      "s.orchids.narcotics": 1,
      beverage: 1,
      nectar: 10,
    },
    label: "Craft: Leathy",
    duration: 90,
    skill: "s.orchids.narcotics",
    result: "leathy",
  },
  {
    id: "craft.scholar.herbs.infusions_beverage_leathy_nectar",
    reqs: {
      ability: 1,
      "s.herbs.infusions": 1,
      beverage: 1,
      nectar: 10,
    },
    label: "Craft: Leathy",
    duration: 90,
    skill: "s.herbs.infusions",
    result: "leathy",
  },
  {
    id: "craft.scholar.insects.nectars_beverage_leathy_nectar",
    reqs: {
      ability: 1,
      "s.insects.nectars": 1,
      beverage: 1,
      nectar: 10,
    },
    label: "Craft: Leathy",
    duration: 90,
    skill: "s.insects.nectars",
    result: "leathy",
  },
  {
    id: "craft.scholar.auroralcontemplations_light_ichor.auroral_rose",
    reqs: {
      ability: 1,
      "s.auroralcontemplations": 1,
      light: 1,
      rose: 10,
    },
    label: "Craft: Ichor Auroral",
    duration: 90,
    skill: "s.auroralcontemplations",
    result: "ichor.auroral",
  },
  {
    id: "craft.scholar.sandstories_light_ichor.auroral_rose",
    reqs: {
      ability: 1,
      "s.sandstories": 1,
      light: 1,
      rose: 10,
    },
    label: "Craft: Ichor Auroral",
    duration: 90,
    skill: "s.sandstories",
    result: "ichor.auroral",
  },
  {
    id: "craft.scholar.sights.sensations_light_ichor.auroral_sky",
    reqs: {
      ability: 1,
      "s.sights.sensations": 1,
      light: 1,
      sky: 10,
    },
    label: "Craft: Ichor Auroral",
    duration: 90,
    skill: "s.sights.sensations",
    result: "ichor.auroral",
  },
  {
    id: "craft.scholar.watchmansparadoxes_light_ichor.auroral_sky",
    reqs: {
      ability: 1,
      "s.watchmansparadoxes": 1,
      light: 1,
      sky: 10,
    },
    label: "Craft: Ichor Auroral",
    duration: 90,
    skill: "s.watchmansparadoxes",
    result: "ichor.auroral",
  },
  {
    id: "craft.scholar.inks.power_light_ichor.auroral_rose",
    reqs: {
      ability: 1,
      "s.inks.power": 1,
      light: 1,
      rose: 10,
    },
    label: "Craft: Ichor Auroral",
    duration: 90,
    skill: "s.inks.power",
    result: "ichor.auroral",
  },
  {
    id: "craft.scholar.preliminalmeter_memory_enduring.reflection_rose",
    reqs: {
      ability: 1,
      "s.preliminalmeter": 1,
      memory: 1,
      rose: 10,
    },
    label: "Craft: Enduring Reflection",
    duration: 90,
    skill: "s.preliminalmeter",
    result: "enduring.reflection",
  },
  {
    id: "craft.scholar.pearl.tide_memory_enduring.reflection_rose",
    reqs: {
      ability: 1,
      "s.pearl.tide": 1,
      memory: 1,
      rose: 10,
    },
    label: "Craft: Enduring Reflection",
    duration: 90,
    skill: "s.pearl.tide",
    result: "enduring.reflection",
  },
  {
    id: "craft.scholar.path.pilgrim_memory_enduring.reflection_rose",
    reqs: {
      ability: 1,
      "s.path.pilgrim": 1,
      memory: 1,
      rose: 10,
    },
    label: "Craft: Enduring Reflection",
    duration: 90,
    skill: "s.path.pilgrim",
    result: "enduring.reflection",
  },
  {
    id: "craft.scholar.skystories_memory_enduring.reflection_rose",
    reqs: {
      ability: 1,
      "s.skystories": 1,
      memory: 1,
      rose: 10,
    },
    label: "Craft: Enduring Reflection",
    duration: 90,
    skill: "s.skystories",
    result: "enduring.reflection",
  },
  {
    id: "craft.scholar.strings.songs_instrument_music.thunderskin_heart",
    reqs: {
      ability: 1,
      "s.strings.songs": 1,
      instrument: 1,
      heart: 10,
    },
    label: "Craft: Thunderskin's Paean",
    duration: 90,
    skill: "s.strings.songs",
    result: "music.thunderskin",
  },
  {
    id: "craft.scholar.hill.hollow_egg_relic.living_scale",
    reqs: {
      ability: 1,
      "s.hill.hollow": 1,
      egg: 1,
      scale: 10,
    },
    label: "Craft: Living Relic",
    duration: 90,
    skill: "s.hill.hollow",
    result: "relic.living",
  },
  {
    id: "craft.scholar.furs.feathers_egg_relic.living_scale",
    reqs: {
      ability: 1,
      "s.furs.feathers": 1,
      egg: 1,
      scale: 10,
    },
    label: "Craft: Living Relic",
    duration: 90,
    skill: "s.furs.feathers",
    result: "relic.living",
  },
  {
    id: "craft.scholar.stonestories_egg_relic.living_scale",
    reqs: {
      ability: 1,
      "s.stonestories": 1,
      egg: 1,
      scale: 10,
    },
    label: "Craft: Living Relic",
    duration: 90,
    skill: "s.stonestories",
    result: "relic.living",
  },
  {
    id: "craft.scholar.pentiments.precursors_egg_relic.living_scale",
    reqs: {
      ability: 1,
      "s.pentiments.precursors": 1,
      egg: 1,
      scale: 10,
    },
    label: "Craft: Living Relic",
    duration: 90,
    skill: "s.pentiments.precursors",
    result: "relic.living",
  },
  {
    id: "craft.scholar.stitching.binding_egg_relic.living_sky",
    reqs: {
      ability: 1,
      "s.stitching.binding": 1,
      egg: 1,
      sky: 10,
    },
    label: "Craft: Living Relic",
    duration: 90,
    skill: "s.stitching.binding",
    result: "relic.living",
  },
  {
    id: "craft.scholar.horns.ivories_memory_old.moment_scale",
    reqs: {
      ability: 1,
      "s.horns.ivories": 1,
      memory: 1,
      scale: 10,
    },
    label: "Craft: Old Moment",
    duration: 90,
    skill: "s.horns.ivories",
    result: "old.moment",
  },
  {
    id: "craft.scholar.path.pilgrim_memory_old.moment_scale",
    reqs: {
      ability: 1,
      "s.path.pilgrim": 1,
      memory: 1,
      scale: 10,
    },
    label: "Craft: Old Moment",
    duration: 90,
    skill: "s.path.pilgrim",
    result: "old.moment",
  },
  {
    id: "craft.scholar.coil.chasm_memory_old.moment_scale",
    reqs: {
      ability: 1,
      "s.coil.chasm": 1,
      memory: 1,
      scale: 10,
    },
    label: "Craft: Old Moment",
    duration: 90,
    skill: "s.coil.chasm",
    result: "old.moment",
  },
  {
    id: "craft.scholar.disciplines.thescar_memory_old.moment_lantern",
    reqs: {
      ability: 1,
      "s.disciplines.thescar": 1,
      memory: 1,
      lantern: 10,
    },
    label: "Craft: Old Moment",
    duration: 90,
    skill: "s.disciplines.thescar",
    result: "old.moment",
  },
  {
    id: "craft.scholar.wolfstories_memory_old.moment_scale",
    reqs: {
      ability: 1,
      "s.wolfstories": 1,
      memory: 1,
      scale: 10,
    },
    label: "Craft: Old Moment",
    duration: 90,
    skill: "s.wolfstories",
    result: "old.moment",
  },
  {
    id: "craft.scholar.ouranoscopy_wood_mazarine.fife_sky",
    reqs: {
      ability: 1,
      "s.ouranoscopy": 1,
      wood: 1,
      sky: 10,
    },
    label: "Craft: Mazarine Fife",
    duration: 90,
    skill: "s.ouranoscopy",
    result: "mazarine.fife",
  },
  {
    id: "craft.scholar.sacralimiae_wood_mazarine.fife_sky",
    reqs: {
      ability: 1,
      "s.sacralimiae": 1,
      wood: 1,
      sky: 10,
    },
    label: "Craft: Mazarine Fife",
    duration: 90,
    skill: "s.sacralimiae",
    result: "mazarine.fife",
  },
  {
    id: "craft.scholar.sacrasolisinvicti_wood_mazarine.fife_sky",
    reqs: {
      ability: 1,
      "s.sacrasolisinvicti": 1,
      wood: 1,
      sky: 10,
    },
    label: "Craft: Mazarine Fife",
    duration: 90,
    skill: "s.sacrasolisinvicti",
    result: "mazarine.fife",
  },
  {
    id: "craft.scholar.applebrighteuphonies_instrument_ascendant.harmony_sky",
    reqs: {
      ability: 1,
      "s.applebrighteuphonies": 1,
      instrument: 1,
      sky: 10,
    },
    label: "Craft: Ascendant Harmony",
    duration: 90,
    skill: "s.applebrighteuphonies",
    result: "ascendant.harmony",
  },
  {
    id: "craft.scholar.skystories_instrument_ascendant.harmony_sky",
    reqs: {
      ability: 1,
      "s.skystories": 1,
      instrument: 1,
      sky: 10,
    },
    label: "Craft: Ascendant Harmony",
    duration: 90,
    skill: "s.skystories",
    result: "ascendant.harmony",
  },
  {
    id: "craft.scholar.furs.feathers_instrument_ascendant.harmony_sky",
    reqs: {
      ability: 1,
      "s.furs.feathers": 1,
      instrument: 1,
      sky: 10,
    },
    label: "Craft: Ascendant Harmony",
    duration: 90,
    skill: "s.furs.feathers",
    result: "ascendant.harmony",
  },
  {
    id: "craft.scholar.strings.songs_instrument_ascendant.harmony_sky",
    reqs: {
      ability: 1,
      "s.strings.songs": 1,
      instrument: 1,
      sky: 10,
    },
    label: "Craft: Ascendant Harmony",
    duration: 90,
    skill: "s.strings.songs",
    result: "ascendant.harmony",
  },
  {
    id: "craft.scholar.bells.brazieries_instrument_ascendant.harmony_sky",
    reqs: {
      ability: 1,
      "s.bells.brazieries": 1,
      instrument: 1,
      sky: 10,
    },
    label: "Craft: Ascendant Harmony",
    duration: 90,
    skill: "s.bells.brazieries",
    result: "ascendant.harmony",
  },
  {
    id: "craft.scholar.maggephenemysteries_flower_liquid.solomon_moon",
    reqs: {
      ability: 1,
      "s.maggephenemysteries": 1,
      flower: 1,
      moon: 10,
    },
    label: "Craft: Solomon's Preparation",
    duration: 90,
    skill: "s.maggephenemysteries",
    result: "liquid.solomon",
  },
  {
    id: "craft.scholar.sights.sensations_flower_liquid.solomon_winter",
    reqs: {
      ability: 1,
      "s.sights.sensations": 1,
      flower: 1,
      winter: 10,
    },
    label: "Craft: Solomon's Preparation",
    duration: 90,
    skill: "s.sights.sensations",
    result: "liquid.solomon",
  },
  {
    id: "craft.scholar.raggedcrossroads_flower_liquid.solomon_winter",
    reqs: {
      ability: 1,
      "s.raggedcrossroads": 1,
      flower: 1,
      winter: 10,
    },
    label: "Craft: Solomon's Preparation",
    duration: 90,
    skill: "s.raggedcrossroads",
    result: "liquid.solomon",
  },
  {
    id: "craft.scholar.edictsinviolable_flower_liquid.solomon_moon",
    reqs: {
      ability: 1,
      "s.edictsinviolable": 1,
      flower: 1,
      moon: 10,
    },
    label: "Craft: Solomon's Preparation",
    duration: 90,
    skill: "s.edictsinviolable",
    result: "liquid.solomon",
  },
  {
    id: "craft.scholar.quenchings.quellings_flower_liquid.solomon_winter",
    reqs: {
      ability: 1,
      "s.quenchings.quellings": 1,
      flower: 1,
      winter: 10,
    },
    label: "Craft: Solomon's Preparation",
    duration: 90,
    skill: "s.quenchings.quellings",
    result: "liquid.solomon",
  },
  {
    id: "craft.scholar.inks.containment_flower_liquid.solomon_winter",
    reqs: {
      ability: 1,
      "s.inks.containment": 1,
      flower: 1,
      winter: 10,
    },
    label: "Craft: Solomon's Preparation",
    duration: 90,
    skill: "s.inks.containment",
    result: "liquid.solomon",
  },
  {
    id: "craft.scholar.rhyme.remembrance_remains_essential.periost_winter",
    reqs: {
      ability: 1,
      "s.rhyme.remembrance": 1,
      remains: 1,
      winter: 10,
    },
    label: "Craft: Essential Periost",
    duration: 90,
    skill: "s.rhyme.remembrance",
    result: "essential.periost",
  },
  {
    id: "craft.scholar.snowstories_remains_essential.periost_winter",
    reqs: {
      ability: 1,
      "s.snowstories": 1,
      remains: 1,
      winter: 10,
    },
    label: "Craft: Essential Periost",
    duration: 90,
    skill: "s.snowstories",
    result: "essential.periost",
  },
  {
    id: "craft.scholar.sickle.eclipse_remains_essential.periost_moon",
    reqs: {
      ability: 1,
      "s.sickle.eclipse": 1,
      remains: 1,
      moon: 10,
    },
    label: "Craft: Essential Periost",
    duration: 90,
    skill: "s.sickle.eclipse",
    result: "essential.periost",
  },
  {
    id: "craft.scholar.sharps_remains_essential.periost_moon",
    reqs: {
      ability: 1,
      "s.sharps": 1,
      remains: 1,
      moon: 10,
    },
    label: "Craft: Essential Periost",
    duration: 90,
    skill: "s.sharps",
    result: "essential.periost",
  },
  {
    id: "craft.scholar.wolfstories_remains_essential.periost_moon",
    reqs: {
      ability: 1,
      "s.wolfstories": 1,
      remains: 1,
      moon: 10,
    },
    label: "Craft: Essential Periost",
    duration: 90,
    skill: "s.wolfstories",
    result: "essential.periost",
  },
  {
    id: "craft.scholar.purifications.exaltations_instrument_ascendant.harmony_sky",
    reqs: {
      ability: 1,
      "s.purifications.exaltations": 1,
      instrument: 1,
      sky: 10,
    },
    label: "Craft: Ascendant Harmony",
    duration: 90,
    skill: "s.purifications.exaltations",
    result: "ascendant.harmony",
  },
  {
    id: "craft.scholar.edictsmartial_memory_wormwood.dream_moon",
    reqs: {
      ability: 1,
      "s.edictsmartial": 1,
      memory: 1,
      moon: 10,
    },
    label: "Craft: Wormwood Dream",
    duration: 90,
    skill: "s.edictsmartial",
    result: "wormwood.dream",
  },
  {
    id: "craft.scholar.glassblowing.vesselcrafting_glass_ichor.vitreous_sky",
    reqs: {
      ability: 1,
      "s.glassblowing.vesselcrafting": 1,
      glass: 1,
      sky: 10,
    },
    label: "Craft: Ichor Vitreous",
    duration: 90,
    skill: "s.glassblowing.vesselcrafting",
    result: "ichor.vitreous",
  },
  {
    id: "craft.edictsmartial_tincture.labhitic_edge",
    reqs: {
      ability: 1,
      "s.edictsmartial": 1,
      edge: 5,
    },
    label: "Craft: Labhitic Tincture",
    duration: 60,
    skill: "s.edictsmartial",
    result: "tincture.labhitic",
  },
  {
    id: "craft.disciplines.thehammer_tincture.labhitic_edge",
    reqs: {
      ability: 1,
      "s.disciplines.thehammer": 1,
      edge: 5,
    },
    label: "Craft: Labhitic Tincture",
    duration: 60,
    skill: "s.disciplines.thehammer",
    result: "tincture.labhitic",
  },
  {
    id: "craft.raggedcrossroads_tincture.labhitic_edge",
    reqs: {
      ability: 1,
      "s.raggedcrossroads": 1,
      edge: 5,
    },
    label: "Craft: Labhitic Tincture",
    duration: 60,
    skill: "s.raggedcrossroads",
    result: "tincture.labhitic",
  },
  {
    id: "craft.sickle.eclipse_tincture.labhitic_edge",
    reqs: {
      ability: 1,
      "s.sickle.eclipse": 1,
      edge: 5,
    },
    label: "Craft: Labhitic Tincture",
    duration: 60,
    skill: "s.sickle.eclipse",
    result: "tincture.labhitic",
  },
  {
    id: "craft.sharps_midnight.mark_moon",
    reqs: {
      ability: 1,
      "s.sharps": 1,
      moon: 5,
    },
    label: "Craft: Midnight Mark",
    duration: 60,
    skill: "s.sharps",
    result: "midnight.mark",
  },
  {
    id: "craft.disciplines.thescar_torgues.cleansing_edge",
    reqs: {
      ability: 1,
      "s.disciplines.thescar": 1,
      edge: 5,
    },
    label: "Craft: Torgue's Cleansing",
    duration: 60,
    skill: "s.disciplines.thescar",
    result: "torgues.cleansing",
  },
  {
    id: "craft.auroralcontemplations_torgues.cleansing_edge",
    reqs: {
      ability: 1,
      "s.auroralcontemplations": 1,
      edge: 5,
    },
    label: "Craft: Torgue's Cleansing",
    duration: 60,
    skill: "s.auroralcontemplations",
    result: "torgues.cleansing",
  },
  {
    id: "craft.sharps_torgues.cleansing_edge",
    reqs: {
      ability: 1,
      "s.sharps": 1,
      edge: 5,
    },
    label: "Craft: Torgue's Cleansing",
    duration: 60,
    skill: "s.sharps",
    result: "torgues.cleansing",
  },
  {
    id: "craft.meniscatereflections_torgues.cleansing_edge",
    reqs: {
      ability: 1,
      "s.meniscatereflections": 1,
      edge: 5,
    },
    label: "Craft: Torgue's Cleansing",
    duration: 60,
    skill: "s.meniscatereflections",
    result: "torgues.cleansing",
  },
  {
    id: "craft.purifications.exaltations_torgues.cleansing_sky",
    reqs: {
      ability: 1,
      "s.purifications.exaltations": 1,
      sky: 5,
    },
    label: "Craft: Torgue's Cleansing",
    duration: 60,
    skill: "s.purifications.exaltations",
    result: "torgues.cleansing",
  },
  {
    id: "craft.bells.brazieries_salts.bitterblack_forge",
    reqs: {
      ability: 1,
      "s.bells.brazieries": 1,
      forge: 5,
    },
    label: "Craft: Bitterblack Salts",
    duration: 60,
    skill: "s.bells.brazieries",
    result: "salts.bitterblack",
  },
  {
    id: "craft.transformations.liberations_salts.bitterblack_forge",
    reqs: {
      ability: 1,
      "s.transformations.liberations": 1,
      forge: 5,
    },
    label: "Craft: Bitterblack Salts",
    duration: 60,
    skill: "s.transformations.liberations",
    result: "salts.bitterblack",
  },
  {
    id: "craft.disciplines.thehammer_salts.bitterblack_forge",
    reqs: {
      ability: 1,
      "s.disciplines.thehammer": 1,
      forge: 5,
    },
    label: "Craft: Bitterblack Salts",
    duration: 60,
    skill: "s.disciplines.thehammer",
    result: "salts.bitterblack",
  },
  {
    id: "craft.door.wall_salts.bitterblack_forge",
    reqs: {
      ability: 1,
      "s.door.wall": 1,
      forge: 5,
    },
    label: "Craft: Bitterblack Salts",
    duration: 60,
    skill: "s.door.wall",
    result: "salts.bitterblack",
  },
  {
    id: "craft.anbary.lapidary_salts.bitterblack_forge",
    reqs: {
      ability: 1,
      "s.anbary.lapidary": 1,
      forge: 5,
    },
    label: "Craft: Bitterblack Salts",
    duration: 60,
    skill: "s.anbary.lapidary",
    result: "salts.bitterblack",
  },
  {
    id: "craft.sacralimiae_material.westcott_moth",
    reqs: {
      ability: 1,
      "s.sacralimiae": 1,
      moth: 5,
    },
    label: "Craft: Westcott's Compounds",
    duration: 60,
    skill: "s.sacralimiae",
    result: "material.westcott",
  },
  {
    id: "craft.solutions.separations_material.westcott_moth",
    reqs: {
      ability: 1,
      "s.solutions.separations": 1,
      moth: 5,
    },
    label: "Craft: Westcott's Compounds",
    duration: 60,
    skill: "s.solutions.separations",
    result: "material.westcott",
  },
  {
    id: "craft.pyroglyphics_material.westcott_forge",
    reqs: {
      ability: 1,
      "s.pyroglyphics": 1,
      forge: 5,
    },
    label: "Craft: Westcott's Compounds",
    duration: 60,
    skill: "s.pyroglyphics",
    result: "material.westcott",
  },
  {
    id: "craft.meniscatereflections_material.westcott_forge",
    reqs: {
      ability: 1,
      "s.meniscatereflections": 1,
      forge: 5,
    },
    label: "Craft: Westcott's Compounds",
    duration: 60,
    skill: "s.meniscatereflections",
    result: "material.westcott",
  },
  {
    id: "craft.spices.savours_material.westcott_forge",
    reqs: {
      ability: 1,
      "s.spices.savours": 1,
      forge: 5,
    },
    label: "Craft: Westcott's Compounds",
    duration: 60,
    skill: "s.spices.savours",
    result: "material.westcott",
  },
  {
    id: "craft.applebrighteuphonies_music.beguiling_grail",
    reqs: {
      ability: 1,
      "s.applebrighteuphonies": 1,
      grail: 5,
    },
    label: "Craft: Beguiling Melody",
    duration: 60,
    skill: "s.applebrighteuphonies",
    result: "music.beguiling",
  },
  {
    id: "craft.resurgences.emergences_music.beguiling_grail",
    reqs: {
      ability: 1,
      "s.resurgences.emergences": 1,
      grail: 5,
    },
    label: "Craft: Beguiling Melody",
    duration: 60,
    skill: "s.resurgences.emergences",
    result: "music.beguiling",
  },
  {
    id: "craft.pentiments.precursors_music.beguiling_grail",
    reqs: {
      ability: 1,
      "s.pentiments.precursors": 1,
      grail: 5,
    },
    label: "Craft: Beguiling Melody",
    duration: 60,
    skill: "s.pentiments.precursors",
    result: "music.beguiling",
  },
  {
    id: "craft.tridesmahiera_music.beguiling_grail",
    reqs: {
      ability: 1,
      "s.tridesmahiera": 1,
      grail: 5,
    },
    label: "Craft: Beguiling Melody",
    duration: 60,
    skill: "s.tridesmahiera",
    result: "music.beguiling",
  },
  {
    id: "craft.orchids.narcotics_witching.tisane_grail",
    reqs: {
      ability: 1,
      "s.orchids.narcotics": 1,
      grail: 5,
    },
    label: "Craft: Witching Tisane",
    duration: 60,
    skill: "s.orchids.narcotics",
    result: "witching.tisane",
  },
  {
    id: "craft.leaves.thorns_witching.tisane_grail",
    reqs: {
      ability: 1,
      "s.leaves.thorns": 1,
      grail: 5,
    },
    label: "Craft: Witching Tisane",
    duration: 60,
    skill: "s.leaves.thorns",
    result: "witching.tisane",
  },
  {
    id: "craft.desires.dissolutions_witching.tisane_grail",
    reqs: {
      ability: 1,
      "s.desires.dissolutions": 1,
      grail: 5,
    },
    label: "Craft: Witching Tisane",
    duration: 60,
    skill: "s.desires.dissolutions",
    result: "witching.tisane",
  },
  {
    id: "craft.thegreatsignsandthegreatscars_witching.tisane_grail",
    reqs: {
      ability: 1,
      "s.thegreatsignsandthegreatscars": 1,
      grail: 5,
    },
    label: "Craft: Witching Tisane",
    duration: 60,
    skill: "s.thegreatsignsandthegreatscars",
    result: "witching.tisane",
  },
  {
    id: "craft.seastories_witching.tisane_grail",
    reqs: {
      ability: 1,
      "s.seastories": 1,
      grail: 5,
    },
    label: "Craft: Witching Tisane",
    duration: 60,
    skill: "s.seastories",
    result: "witching.tisane",
  },
  {
    id: "craft.strings.songs_music.cheerful_heart",
    reqs: {
      ability: 1,
      "s.strings.songs": 1,
      heart: 5,
    },
    label: "Craft: Cheerful Ditty",
    duration: 60,
    skill: "s.strings.songs",
    result: "music.cheerful",
  },
  {
    id: "craft.drums.dances_music.cheerful_heart",
    reqs: {
      ability: 1,
      "s.drums.dances": 1,
      heart: 5,
    },
    label: "Craft: Cheerful Ditty",
    duration: 60,
    skill: "s.drums.dances",
    result: "music.cheerful",
  },
  {
    id: "craft.stitching.binding_perhibiate_heart",
    reqs: {
      ability: 1,
      "s.stitching.binding": 1,
      heart: 5,
    },
    label: "Craft: Perhibiate",
    duration: 60,
    skill: "s.stitching.binding",
    result: "perhibiate",
  },
  {
    id: "craft.weaving.knotworking_perhibiate_heart",
    reqs: {
      ability: 1,
      "s.weaving.knotworking": 1,
      heart: 5,
    },
    label: "Craft: Perhibiate",
    duration: 60,
    skill: "s.weaving.knotworking",
    result: "perhibiate",
  },
  {
    id: "craft.inks.power_perhibiate_scale",
    reqs: {
      ability: 1,
      "s.inks.power": 1,
      scale: 5,
    },
    label: "Craft: Perhibiate",
    duration: 60,
    skill: "s.inks.power",
    result: "perhibiate",
  },
  {
    id: "craft.herbs.infusions_liquid.regensburg_heart",
    reqs: {
      ability: 1,
      "s.herbs.infusions": 1,
      heart: 5,
    },
    label: "Craft: Regensburg Balm",
    duration: 60,
    skill: "s.herbs.infusions",
    result: "liquid.regensburg",
  },
  {
    id: "craft.quenchings.quellings_liquid.regensburg_heart",
    reqs: {
      ability: 1,
      "s.quenchings.quellings": 1,
      heart: 5,
    },
    label: "Craft: Regensburg Balm",
    duration: 60,
    skill: "s.quenchings.quellings",
    result: "liquid.regensburg",
  },
  {
    id: "craft.surgeries.exsanguinations_liquid.regensburg_heart",
    reqs: {
      ability: 1,
      "s.surgeries.exsanguinations": 1,
      heart: 5,
    },
    label: "Craft: Regensburg Balm",
    duration: 60,
    skill: "s.surgeries.exsanguinations",
    result: "liquid.regensburg",
  },
  {
    id: "craft.maggephenemysteries_liquid.regensburg_heart",
    reqs: {
      ability: 1,
      "s.maggephenemysteries": 1,
      heart: 5,
    },
    label: "Craft: Regensburg Balm",
    duration: 60,
    skill: "s.maggephenemysteries",
    result: "liquid.regensburg",
  },
  {
    id: "craft.edictsinviolable_liquid.regensburg_heart",
    reqs: {
      ability: 1,
      "s.edictsinviolable": 1,
      heart: 5,
    },
    label: "Craft: Regensburg Balm",
    duration: 60,
    skill: "s.edictsinviolable",
    result: "liquid.regensburg",
  },
  {
    id: "craft.edictsliminal_secret.threshold_knock",
    reqs: {
      ability: 1,
      "s.edictsliminal": 1,
      knock: 5,
    },
    label: "Craft: Secret Threshold",
    duration: 60,
    skill: "s.edictsliminal",
    result: "secret.threshold",
  },
  {
    id: "craft.door.wall_secret.threshold_knock",
    reqs: {
      ability: 1,
      "s.door.wall": 1,
      knock: 5,
    },
    label: "Craft: Secret Threshold",
    duration: 60,
    skill: "s.door.wall",
    result: "secret.threshold",
  },
  {
    id: "craft.horns.ivories_secret.threshold_knock",
    reqs: {
      ability: 1,
      "s.horns.ivories": 1,
      knock: 5,
    },
    label: "Craft: Secret Threshold",
    duration: 60,
    skill: "s.horns.ivories",
    result: "secret.threshold",
  },
  {
    id: "craft.preliminalmeter_secret.threshold_knock",
    reqs: {
      ability: 1,
      "s.preliminalmeter": 1,
      knock: 5,
    },
    label: "Craft: Secret Threshold",
    duration: 60,
    skill: "s.preliminalmeter",
    result: "secret.threshold",
  },
  {
    id: "craft.glassblowing.vesselcrafting_ampoule.amethyst_knock",
    reqs: {
      ability: 1,
      "s.glassblowing.vesselcrafting": 1,
      knock: 5,
    },
    label: "Craft: Amethyst Ampoule",
    duration: 60,
    skill: "s.glassblowing.vesselcrafting",
    result: "ampoule.amethyst",
  },
  {
    id: "craft.surgeries.exsanguinations_ampoule.amethyst_knock",
    reqs: {
      ability: 1,
      "s.surgeries.exsanguinations": 1,
      knock: 5,
    },
    label: "Craft: Amethyst Ampoule",
    duration: 60,
    skill: "s.surgeries.exsanguinations",
    result: "ampoule.amethyst",
  },
  {
    id: "craft.solutions.separations_ampoule.amethyst_knock",
    reqs: {
      ability: 1,
      "s.solutions.separations": 1,
      knock: 5,
    },
    label: "Craft: Amethyst Ampoule",
    duration: 60,
    skill: "s.solutions.separations",
    result: "ampoule.amethyst",
  },
  {
    id: "craft.lockworks.clockworks_ampoule.amethyst_knock",
    reqs: {
      ability: 1,
      "s.lockworks.clockworks": 1,
      knock: 5,
    },
    label: "Craft: Amethyst Ampoule",
    duration: 60,
    skill: "s.lockworks.clockworks",
    result: "ampoule.amethyst",
  },
  {
    id: "craft.thegreatsignsandthegreatscars_ampoule.amethyst_knock",
    reqs: {
      ability: 1,
      "s.thegreatsignsandthegreatscars": 1,
      knock: 5,
    },
    label: "Craft: Amethyst Ampoule",
    duration: 60,
    skill: "s.thegreatsignsandthegreatscars",
    result: "ampoule.amethyst",
  },
  {
    id: "craft.purifications.exaltations_pigment.refulgin_lantern",
    reqs: {
      ability: 1,
      "s.purifications.exaltations": 1,
      lantern: 5,
    },
    label: "Craft: Refulgin",
    duration: 60,
    skill: "s.purifications.exaltations",
    result: "pigment.refulgin",
  },
  {
    id: "craft.transformations.liberations_pigment.refulgin_lantern",
    reqs: {
      ability: 1,
      "s.transformations.liberations": 1,
      lantern: 5,
    },
    label: "Craft: Refulgin",
    duration: 60,
    skill: "s.transformations.liberations",
    result: "pigment.refulgin",
  },
  {
    id: "craft.sacrasolisinvicti_pigment.refulgin_lantern",
    reqs: {
      ability: 1,
      "s.sacrasolisinvicti": 1,
      lantern: 5,
    },
    label: "Craft: Refulgin",
    duration: 60,
    skill: "s.sacrasolisinvicti",
    result: "pigment.refulgin",
  },
  {
    id: "craft.inks.revelation_pigment.refulgin_lantern",
    reqs: {
      ability: 1,
      "s.inks.revelation": 1,
      lantern: 5,
    },
    label: "Craft: Refulgin",
    duration: 60,
    skill: "s.inks.revelation",
    result: "pigment.refulgin",
  },
  {
    id: "craft.glaziery.lightsmithing_dearday.lens_lantern",
    reqs: {
      ability: 1,
      "s.glaziery.lightsmithing": 1,
      lantern: 5,
    },
    label: "Craft: Dearday Lens",
    duration: 60,
    skill: "s.glaziery.lightsmithing",
    result: "dearday.lens",
  },
  {
    id: "craft.glassblowing.vesselcrafting_dearday.lens_lantern",
    reqs: {
      ability: 1,
      "s.glassblowing.vesselcrafting": 1,
      lantern: 5,
    },
    label: "Craft: Dearday Lens",
    duration: 60,
    skill: "s.glassblowing.vesselcrafting",
    result: "dearday.lens",
  },
  {
    id: "craft.pyroglyphics_dearday.lens_lantern",
    reqs: {
      ability: 1,
      "s.pyroglyphics": 1,
      lantern: 5,
    },
    label: "Craft: Dearday Lens",
    duration: 60,
    skill: "s.pyroglyphics",
    result: "dearday.lens",
  },
  {
    id: "craft.disciplines.thescar_dearday.lens_lantern",
    reqs: {
      ability: 1,
      "s.disciplines.thescar": 1,
      lantern: 5,
    },
    label: "Craft: Dearday Lens",
    duration: 60,
    skill: "s.disciplines.thescar",
    result: "dearday.lens",
  },
  {
    id: "craft.desires.dissolutions_midnight.mark_moon",
    reqs: {
      ability: 1,
      "s.desires.dissolutions": 1,
      moon: 5,
    },
    label: "Craft: Midnight Mark",
    duration: 60,
    skill: "s.desires.dissolutions",
    result: "midnight.mark",
  },
  {
    id: "craft.snowstories_midnight.mark_moon",
    reqs: {
      ability: 1,
      "s.snowstories": 1,
      moon: 5,
    },
    label: "Craft: Midnight Mark",
    duration: 60,
    skill: "s.snowstories",
    result: "midnight.mark",
  },
  {
    id: "craft.serpents.venoms_midnight.mark_moon",
    reqs: {
      ability: 1,
      "s.serpents.venoms": 1,
      moon: 5,
    },
    label: "Craft: Midnight Mark",
    duration: 60,
    skill: "s.serpents.venoms",
    result: "midnight.mark",
  },
  {
    id: "craft.insects.nectars_midnight.mark_moon",
    reqs: {
      ability: 1,
      "s.insects.nectars": 1,
      moon: 5,
    },
    label: "Craft: Midnight Mark",
    duration: 60,
    skill: "s.insects.nectars",
    result: "midnight.mark",
  },
  {
    id: "craft.wolfstories_midnight.mark_moon",
    reqs: {
      ability: 1,
      "s.wolfstories": 1,
      moon: 5,
    },
    label: "Craft: Midnight Mark",
    duration: 60,
    skill: "s.wolfstories",
    result: "midnight.mark",
  },
  {
    id: "craft.rhyme.remembrance_midnight.mark_moon",
    reqs: {
      ability: 1,
      "s.rhyme.remembrance": 1,
      moon: 5,
    },
    label: "Craft: Midnight Mark",
    duration: 60,
    skill: "s.rhyme.remembrance",
    result: "midnight.mark",
  },
  {
    id: "craft.inks.containment_catwink_moon",
    reqs: {
      ability: 1,
      "s.inks.containment": 1,
      moon: 5,
    },
    label: "Craft: Catwink",
    duration: 60,
    skill: "s.inks.containment",
    result: "catwink",
  },
  {
    id: "craft.sickle.eclipse_catwink_moon",
    reqs: {
      ability: 1,
      "s.sickle.eclipse": 1,
      moon: 5,
    },
    label: "Craft: Catwink",
    duration: 60,
    skill: "s.sickle.eclipse",
    result: "catwink",
  },
  {
    id: "craft.sandstories_catwink_moon",
    reqs: {
      ability: 1,
      "s.sandstories": 1,
      moon: 5,
    },
    label: "Craft: Catwink",
    duration: 60,
    skill: "s.sandstories",
    result: "catwink",
  },
  {
    id: "craft.putrefactions.calcinations_catwink_moon",
    reqs: {
      ability: 1,
      "s.putrefactions.calcinations": 1,
      moon: 5,
    },
    label: "Craft: Catwink",
    duration: 60,
    skill: "s.putrefactions.calcinations",
    result: "catwink",
  },
  {
    id: "craft.edictsinviolable_catwink_moon",
    reqs: {
      ability: 1,
      "s.edictsinviolable": 1,
      moon: 5,
    },
    label: "Craft: Catwink",
    duration: 60,
    skill: "s.edictsinviolable",
    result: "catwink",
  },
  {
    id: "craft.inks.revelation_paint.moth_moth",
    reqs: {
      ability: 1,
      "s.inks.revelation": 1,
      moth: 5,
    },
    label: "Craft: Paint: Moth-Gold",
    duration: 60,
    skill: "s.inks.revelation",
    result: "paint.moth",
  },
  {
    id: "craft.edictsliminal_tanglebrag_moth",
    reqs: {
      ability: 1,
      "s.edictsliminal": 1,
      moth: 5,
    },
    label: "Craft: Tanglebrag",
    duration: 60,
    skill: "s.edictsliminal",
    result: "tanglebrag",
  },
  {
    id: "craft.resurgences.emergences_tanglebrag_moth",
    reqs: {
      ability: 1,
      "s.resurgences.emergences": 1,
      moth: 5,
    },
    label: "Craft: Tanglebrag",
    duration: 60,
    skill: "s.resurgences.emergences",
    result: "tanglebrag",
  },
  {
    id: "craft.watchmansparadoxes_tanglebrag_lantern",
    reqs: {
      ability: 1,
      "s.watchmansparadoxes": 1,
      lantern: 5,
    },
    label: "Craft: Tanglebrag",
    duration: 60,
    skill: "s.watchmansparadoxes",
    result: "tanglebrag",
  },
  {
    id: "craft.rites.theroots_tanglebrag_moth",
    reqs: {
      ability: 1,
      "s.rites.theroots": 1,
      moth: 5,
    },
    label: "Craft: Tanglebrag",
    duration: 60,
    skill: "s.rites.theroots",
    result: "tanglebrag",
  },
  {
    id: "craft.weaving.knotworking_tanglebrag_moth",
    reqs: {
      ability: 1,
      "s.weaving.knotworking": 1,
      moth: 5,
    },
    label: "Craft: Tanglebrag",
    duration: 60,
    skill: "s.weaving.knotworking",
    result: "tanglebrag",
  },
  {
    id: "craft.herbs.infusions_liquid.thirza_nectar",
    reqs: {
      ability: 1,
      "s.herbs.infusions": 1,
      nectar: 5,
    },
    label: "Craft: Thirza's Cordials",
    duration: 60,
    skill: "s.herbs.infusions",
    result: "liquid.thirza",
  },
  {
    id: "craft.coil.chasm_liquid.thirza_nectar",
    reqs: {
      ability: 1,
      "s.coil.chasm": 1,
      nectar: 5,
    },
    label: "Craft: Thirza's Cordials",
    duration: 60,
    skill: "s.coil.chasm",
    result: "liquid.thirza",
  },
  {
    id: "craft.spices.savours_liquid.thirza_nectar",
    reqs: {
      ability: 1,
      "s.spices.savours": 1,
      nectar: 5,
    },
    label: "Craft: Thirza's Cordials",
    duration: 60,
    skill: "s.spices.savours",
    result: "liquid.thirza",
  },
  {
    id: "craft.orchids.narcotics_liquid.thirza_nectar",
    reqs: {
      ability: 1,
      "s.orchids.narcotics": 1,
      nectar: 5,
    },
    label: "Craft: Thirza's Cordials",
    duration: 60,
    skill: "s.orchids.narcotics",
    result: "liquid.thirza",
  },
  {
    id: "craft.stonestories_liquid.thirza_nectar",
    reqs: {
      ability: 1,
      "s.stonestories": 1,
      nectar: 5,
    },
    label: "Craft: Thirza's Cordials",
    duration: 60,
    skill: "s.stonestories",
    result: "liquid.thirza",
  },
  {
    id: "craft.insects.nectars_yewgall.ink_nectar",
    reqs: {
      ability: 1,
      "s.insects.nectars": 1,
      nectar: 5,
    },
    label: "Craft: Yewgall Ink",
    duration: 60,
    skill: "s.insects.nectars",
    result: "yewgall.ink",
  },
  {
    id: "craft.rites.theroots_yewgall.ink_nectar",
    reqs: {
      ability: 1,
      "s.rites.theroots": 1,
      nectar: 5,
    },
    label: "Craft: Yewgall Ink",
    duration: 60,
    skill: "s.rites.theroots",
    result: "yewgall.ink",
  },
  {
    id: "craft.leaves.thorns_yewgall.ink_nectar",
    reqs: {
      ability: 1,
      "s.leaves.thorns": 1,
      nectar: 5,
    },
    label: "Craft: Yewgall Ink",
    duration: 60,
    skill: "s.leaves.thorns",
    result: "yewgall.ink",
  },
  {
    id: "craft.putrefactions.calcinations_yewgall.ink_winter",
    reqs: {
      ability: 1,
      "s.putrefactions.calcinations": 1,
      winter: 5,
    },
    label: "Craft: Yewgall Ink",
    duration: 60,
    skill: "s.putrefactions.calcinations",
    result: "yewgall.ink",
  },
  {
    id: "craft.preliminalmeter_confounding.parable_rose",
    reqs: {
      ability: 1,
      "s.preliminalmeter": 1,
      rose: 5,
    },
    label: "Craft: Confounding Parable",
    duration: 60,
    skill: "s.preliminalmeter",
    result: "confounding.parable",
  },
  {
    id: "craft.skystories_confounding.parable_rose",
    reqs: {
      ability: 1,
      "s.skystories": 1,
      rose: 5,
    },
    label: "Craft: Confounding Parable",
    duration: 60,
    skill: "s.skystories",
    result: "confounding.parable",
  },
  {
    id: "craft.tridesmahiera_confounding.parable_moon",
    reqs: {
      ability: 1,
      "s.tridesmahiera": 1,
      moon: 5,
    },
    label: "Craft: Confounding Parable",
    duration: 60,
    skill: "s.tridesmahiera",
    result: "confounding.parable",
  },
  {
    id: "craft.applebrighteuphonies_confounding.parable_sky",
    reqs: {
      ability: 1,
      "s.applebrighteuphonies": 1,
      sky: 5,
    },
    label: "Craft: Confounding Parable",
    duration: 60,
    skill: "s.applebrighteuphonies",
    result: "confounding.parable",
  },
  {
    id: "craft.sacrasolisinvicti_confounding.parable_sky",
    reqs: {
      ability: 1,
      "s.sacrasolisinvicti": 1,
      sky: 5,
    },
    label: "Craft: Confounding Parable",
    duration: 60,
    skill: "s.sacrasolisinvicti",
    result: "confounding.parable",
  },
  {
    id: "craft.pearl.tide_sign.salt_rose",
    reqs: {
      ability: 1,
      "s.pearl.tide": 1,
      rose: 5,
    },
    label: "Craft: Salt-Sign",
    duration: 60,
    skill: "s.pearl.tide",
    result: "sign.salt",
  },
  {
    id: "craft.seastories_sign.salt_moon",
    reqs: {
      ability: 1,
      "s.seastories": 1,
      moon: 5,
    },
    label: "Craft: Salt-Sign",
    duration: 60,
    skill: "s.seastories",
    result: "sign.salt",
  },
  {
    id: "craft.path.pilgrim_sign.salt_rose",
    reqs: {
      ability: 1,
      "s.path.pilgrim": 1,
      rose: 5,
    },
    label: "Craft: Salt-Sign",
    duration: 60,
    skill: "s.path.pilgrim",
    result: "sign.salt",
  },
  {
    id: "craft.sandstories_sign.salt_rose",
    reqs: {
      ability: 1,
      "s.sandstories": 1,
      rose: 5,
    },
    label: "Craft: Salt-Sign",
    duration: 60,
    skill: "s.sandstories",
    result: "sign.salt",
  },
  {
    id: "craft.inks.power_sign.salt_rose",
    reqs: {
      ability: 1,
      "s.inks.power": 1,
      rose: 5,
    },
    label: "Craft: Salt-Sign",
    duration: 60,
    skill: "s.inks.power",
    result: "sign.salt",
  },
  {
    id: "craft.hill.hollow_sign.salt_moon",
    reqs: {
      ability: 1,
      "s.hill.hollow": 1,
      moon: 5,
    },
    label: "Craft: Salt-Sign",
    duration: 60,
    skill: "s.hill.hollow",
    result: "sign.salt",
  },
  {
    id: "craft.path.pilgrim_bisclavrets.knot_scale",
    reqs: {
      ability: 1,
      "s.path.pilgrim": 1,
      scale: 5,
    },
    label: "Craft: Bisclavret's Knot",
    duration: 60,
    skill: "s.path.pilgrim",
    result: "bisclavrets.knot",
  },
  {
    id: "craft.serpents.venoms_bisclavrets.knot_scale",
    reqs: {
      ability: 1,
      "s.serpents.venoms": 1,
      scale: 5,
    },
    label: "Craft: Bisclavret's Knot",
    duration: 60,
    skill: "s.serpents.venoms",
    result: "bisclavrets.knot",
  },
  {
    id: "craft.coil.chasm_bisclavrets.knot_scale",
    reqs: {
      ability: 1,
      "s.coil.chasm": 1,
      scale: 5,
    },
    label: "Craft: Bisclavret's Knot",
    duration: 60,
    skill: "s.coil.chasm",
    result: "bisclavrets.knot",
  },
  {
    id: "craft.stonestories_music.savage_scale",
    reqs: {
      ability: 1,
      "s.stonestories": 1,
      scale: 5,
    },
    label: "Craft: Savage Hymn",
    duration: 60,
    skill: "s.stonestories",
    result: "music.savage",
  },
  {
    id: "craft.drums.dances_music.savage_scale",
    reqs: {
      ability: 1,
      "s.drums.dances": 1,
      scale: 5,
    },
    label: "Craft: Savage Hymn",
    duration: 60,
    skill: "s.drums.dances",
    result: "music.savage",
  },
  {
    id: "craft.horns.ivories_bisclavrets.knot_scale",
    reqs: {
      ability: 1,
      "s.horns.ivories": 1,
      scale: 5,
    },
    label: "Craft: Bisclavret's Knot",
    duration: 60,
    skill: "s.horns.ivories",
    result: "bisclavrets.knot",
  },
  {
    id: "craft.furs.feathers_bisclavrets.knot_scale",
    reqs: {
      ability: 1,
      "s.furs.feathers": 1,
      scale: 5,
    },
    label: "Craft: Bisclavret's Knot",
    duration: 60,
    skill: "s.furs.feathers",
    result: "bisclavrets.knot",
  },
  {
    id: "craft.wolfstories_bisclavrets.knot_scale",
    reqs: {
      ability: 1,
      "s.wolfstories": 1,
      scale: 5,
    },
    label: "Craft: Bisclavret's Knot",
    duration: 60,
    skill: "s.wolfstories",
    result: "bisclavrets.knot",
  },
  {
    id: "craft.pentiments.precursors_bisclavrets.knot_scale",
    reqs: {
      ability: 1,
      "s.pentiments.precursors": 1,
      scale: 5,
    },
    label: "Craft: Bisclavret's Knot",
    duration: 60,
    skill: "s.pentiments.precursors",
    result: "bisclavrets.knot",
  },
  {
    id: "craft.hill.hollow_bisclavrets.knot_scale",
    reqs: {
      ability: 1,
      "s.hill.hollow": 1,
      scale: 5,
    },
    label: "Craft: Bisclavret's Knot",
    duration: 60,
    skill: "s.hill.hollow",
    result: "bisclavrets.knot",
  },
  {
    id: "craft.anbary.lapidary_awakened.feather_sky",
    reqs: {
      ability: 1,
      "s.anbary.lapidary": 1,
      sky: 5,
    },
    label: "Craft: Awakened Feather",
    duration: 60,
    skill: "s.anbary.lapidary",
    result: "awakened.feather",
  },
  {
    id: "craft.lockworks.clockworks_awakened.feather_sky",
    reqs: {
      ability: 1,
      "s.lockworks.clockworks": 1,
      sky: 5,
    },
    label: "Craft: Awakened Feather",
    duration: 60,
    skill: "s.lockworks.clockworks",
    result: "awakened.feather",
  },
  {
    id: "craft.stitching.binding_awakened.feather_sky",
    reqs: {
      ability: 1,
      "s.stitching.binding": 1,
      sky: 5,
    },
    label: "Craft: Awakened Feather",
    duration: 60,
    skill: "s.stitching.binding",
    result: "awakened.feather",
  },
  {
    id: "craft.sights.sensations_awakened.feather_sky",
    reqs: {
      ability: 1,
      "s.sights.sensations": 1,
      sky: 5,
    },
    label: "Craft: Awakened Feather",
    duration: 60,
    skill: "s.sights.sensations",
    result: "awakened.feather",
  },
  {
    id: "craft.sacralimiae_awakened.feather_sky",
    reqs: {
      ability: 1,
      "s.sacralimiae": 1,
      sky: 5,
    },
    label: "Craft: Awakened Feather",
    duration: 60,
    skill: "s.sacralimiae",
    result: "awakened.feather",
  },
  {
    id: "craft.strings.songs_music.wistful_sky",
    reqs: {
      ability: 1,
      "s.strings.songs": 1,
      sky: 5,
    },
    label: "Craft: Wistful Air",
    duration: 60,
    skill: "s.strings.songs",
    result: "music.wistful",
  },
  {
    id: "craft.bells.brazieries_music.wistful_sky",
    reqs: {
      ability: 1,
      "s.bells.brazieries": 1,
      sky: 5,
    },
    label: "Craft: Wistful Air",
    duration: 60,
    skill: "s.bells.brazieries",
    result: "music.wistful",
  },
  {
    id: "craft.ouranoscopy_music.wistful_sky",
    reqs: {
      ability: 1,
      "s.ouranoscopy": 1,
      sky: 5,
    },
    label: "Craft: Wistful Air",
    duration: 60,
    skill: "s.ouranoscopy",
    result: "music.wistful",
  },
  {
    id: "craft.watchmansparadoxes_music.wistful_sky",
    reqs: {
      ability: 1,
      "s.watchmansparadoxes": 1,
      sky: 5,
    },
    label: "Craft: Wistful Air",
    duration: 60,
    skill: "s.watchmansparadoxes",
    result: "music.wistful",
  },
  {
    id: "craft.skystories_music.wistful_sky",
    reqs: {
      ability: 1,
      "s.skystories": 1,
      sky: 5,
    },
    label: "Craft: Wistful Air",
    duration: 60,
    skill: "s.skystories",
    result: "music.wistful",
  },
  {
    id: "craft.ouranoscopy_bittersweet.certainty_moon",
    reqs: {
      ability: 1,
      "s.ouranoscopy": 1,
      moon: 5,
    },
    label: "Craft: Bittersweet Certainty",
    duration: 60,
    skill: "s.ouranoscopy",
    result: "bittersweet.certainty",
  },
  {
    id: "craft.rhyme.remembrance_bittersweet.certainty_winter",
    reqs: {
      ability: 1,
      "s.rhyme.remembrance": 1,
      winter: 5,
    },
    label: "Craft: Bittersweet Certainty",
    duration: 60,
    skill: "s.rhyme.remembrance",
    result: "bittersweet.certainty",
  },
  {
    id: "craft.raggedcrossroads_bittersweet.certainty_winter",
    reqs: {
      ability: 1,
      "s.raggedcrossroads": 1,
      winter: 5,
    },
    label: "Craft: Bittersweet Certainty",
    duration: 60,
    skill: "s.raggedcrossroads",
    result: "bittersweet.certainty",
  },
  {
    id: "craft.snowstories_bittersweet.certainty_winter",
    reqs: {
      ability: 1,
      "s.snowstories": 1,
      winter: 5,
    },
    label: "Craft: Bittersweet Certainty",
    duration: 60,
    skill: "s.snowstories",
    result: "bittersweet.certainty",
  },
  {
    id: "craft.auroralcontemplations_eigengrau_lantern",
    reqs: {
      ability: 1,
      "s.auroralcontemplations": 1,
      lantern: 5,
    },
    label: "Craft: Eigengrau",
    duration: 60,
    skill: "s.auroralcontemplations",
    result: "eigengrau",
  },
  {
    id: "craft.sights.sensations_eigengrau_winter",
    reqs: {
      ability: 1,
      "s.sights.sensations": 1,
      winter: 5,
    },
    label: "Craft: Eigengrau",
    duration: 60,
    skill: "s.sights.sensations",
    result: "eigengrau",
  },
  {
    id: "craft.quenchings.quellings_eigengrau_winter",
    reqs: {
      ability: 1,
      "s.quenchings.quellings": 1,
      winter: 5,
    },
    label: "Craft: Eigengrau",
    duration: 60,
    skill: "s.quenchings.quellings",
    result: "eigengrau",
  },
  {
    id: "craft.inks.containment_eigengrau_winter",
    reqs: {
      ability: 1,
      "s.inks.containment": 1,
      winter: 5,
    },
    label: "Craft: Eigengrau",
    duration: 60,
    skill: "s.inks.containment",
    result: "eigengrau",
  },
  {
    id: "craft.maggephenemysteries_eigengrau_moon",
    reqs: {
      ability: 1,
      "s.maggephenemysteries": 1,
      moon: 5,
    },
    label: "Craft: Eigengrau",
    duration: 60,
    skill: "s.maggephenemysteries",
    result: "eigengrau",
  },
  {
    id: "craft.pearl.tide_eigengrau_moon",
    reqs: {
      ability: 1,
      "s.pearl.tide": 1,
      moon: 5,
    },
    label: "Craft: Eigengrau",
    duration: 60,
    skill: "s.pearl.tide",
    result: "eigengrau",
  },
  {
    id: "craft.glaziery.lightsmithing_dearday.lens_sky",
    reqs: {
      ability: 1,
      "s.glaziery.lightsmithing": 1,
      sky: 5,
    },
    label: "Craft: Dearday Lens",
    duration: 60,
    skill: "s.glaziery.lightsmithing",
    result: "dearday.lens",
  },
  {
    id: "craft.edictsmartial_old.wound_moon",
    reqs: {
      ability: 1,
      "s.edictsmartial": 1,
      moon: 5,
    },
    label: "Craft: Old Wound",
    duration: 60,
    skill: "s.edictsmartial",
    result: "old.wound",
  },
  {
    id: "craft.furs.feathers_awakened.feather_sky",
    reqs: {
      ability: 1,
      "s.furs.feathers": 1,
      sky: 5,
    },
    label: "Craft: Awakened Feather",
    duration: 60,
    skill: "s.furs.feathers",
    result: "awakened.feather",
  },
  {
    id: "craft.inks.power_yewgall.ink_nectar",
    reqs: {
      ability: 1,
      "s.inks.power": 1,
      nectar: 5,
    },
    label: "Craft: Yewgall Ink",
    duration: 60,
    skill: "s.inks.power",
    result: "yewgall.ink",
  },
  {
    id: "craft.inks.power_stargall.ink_sky",
    reqs: {
      ability: 1,
      "s.inks.power": 1,
      sky: 5,
    },
    label: "Craft: Stargall Ink",
    duration: 60,
    skill: "s.inks.power",
    result: "stargall.ink",
  },
  {
    id: "craft.inks.revelation_yewgall.ink_nectar",
    reqs: {
      ability: 1,
      "s.inks.revelation": 1,
      nectar: 5,
    },
    label: "Craft: Yewgall Ink",
    duration: 60,
    skill: "s.inks.revelation",
    result: "yewgall.ink",
  },
  {
    id: "craft.inks.revelation_stargall.ink_sky",
    reqs: {
      ability: 1,
      "s.inks.revelation": 1,
      sky: 5,
    },
    label: "Craft: Stargall Ink",
    duration: 60,
    skill: "s.inks.revelation",
    result: "stargall.ink",
  },
  {
    id: "craft.inks.containment_yewgall.ink_nectar",
    reqs: {
      ability: 1,
      "s.inks.containment": 1,
      nectar: 5,
    },
    label: "Craft: Yewgall Ink",
    duration: 60,
    skill: "s.inks.containment",
    result: "yewgall.ink",
  },
  {
    id: "craft.inks.containment_stargall.ink_sky",
    reqs: {
      ability: 1,
      "s.inks.containment": 1,
      sky: 5,
    },
    label: "Craft: Stargall Ink",
    duration: 60,
    skill: "s.inks.containment",
    result: "stargall.ink",
  },
  {
    id: "craft.drums.dances_music.savage_nectar",
    reqs: {
      ability: 1,
      "s.drums.dances": 1,
      nectar: 5,
    },
    label: "Craft: Savage Hymn",
    duration: 60,
    skill: "s.drums.dances",
    result: "music.savage",
  },
  {
    id: "craft.glassblowing.vesselcrafting_dearday.lens_sky",
    reqs: {
      ability: 1,
      "s.glassblowing.vesselcrafting": 1,
      sky: 5,
    },
    label: "Craft: Dearday Lens",
    duration: 60,
    skill: "s.glassblowing.vesselcrafting",
    result: "dearday.lens",
  },
  {
    id: "craft.ouranoscopy_bittersweet.certainty_winter",
    reqs: {
      ability: 1,
      "s.ouranoscopy": 1,
      winter: 5,
    },
    label: "Craft: Bittersweet Certainty",
    duration: 60,
    skill: "s.ouranoscopy",
    result: "bittersweet.certainty",
  },
  {
    id: "chandlery.aglaophotis",
    reqs: {
      ability: 1,
      "skill.chandlery": 1,
      beeswax: 1,
      aglaophotis: 1,
    },
    duration: 60,
    skill: "s.undefined",
    result: "candle.aglaophotis",
  },
  {
    id: "chandlery.fragrant.chalice",
    reqs: {
      ability: 1,
      "skill.chandlery": 1,
      beeswax: 1,
      "fragrant.chalice": 1,
    },
    duration: 60,
    skill: "s.undefined",
    result: "candle.fragrant.chalice",
  },
  {
    id: "chandlery.historywax",
    reqs: {
      ability: 1,
      "skill.chandlery": 1,
      beeswax: 1,
      "agave.aeterna": 1,
    },
    duration: 60,
    skill: "s.undefined",
    result: "candle.historywax",
  },
  {
    id: "chandlery.honeyscar",
    reqs: {
      ability: 1,
      "skill.chandlery": 1,
      beeswax: 1,
      "honeyscar.jasmine": 1,
    },
    duration: 60,
    skill: "s.undefined",
    result: "candle.honeyscar",
  },
  {
    id: "chandlery.mothorchid",
    reqs: {
      ability: 1,
      "skill.chandlery": 1,
      beeswax: 1,
      "moth.orchid": 1,
    },
    duration: 60,
    skill: "s.undefined",
    result: "candle.mothorchid",
  },
  {
    id: "chandlery.pyrus",
    reqs: {
      ability: 1,
      "skill.chandlery": 1,
      beeswax: 1,
      "pyrus.auricalcinus": 1,
    },
    duration: 60,
    skill: "s.undefined",
    result: "candle.pyrus",
  },
  {
    id: "chandlery.beeswax",
    reqs: {
      ability: 1,
      "skill.chandlery": 1,
      beeswax: 1,
    },
    duration: 60,
    skill: "s.undefined",
    result: "candle.beeswax",
  },
] as any as Recipe[]; // Bad!
